// src/pages/TrackApplications.jsx
import React, { useMemo, useEffect, useState, useRef, useLayoutEffect, useCallback } from "react";
import { createPortal } from "react-dom";
import {
  CheckCircle,
  Clock,
  Check,
  FileText,
  AlertCircle,
  Calendar,
  Mail,
  Download,
  ChevronRight,
  ArrowLeft,
  Loader2,
  RefreshCw,
  X,
  Pencil,
  Trash2,
  Info,
  PartyPopper,
} from "lucide-react";
import confetti from "canvas-confetti";
import Hero from "../../components/ui/Hero.jsx";

import { getTrackApplicationsByApplicant } from "../../api/borrowerPortal/TrackApplications.js";
import { getStageTimelineColors } from "../../utils/applicationStages.js";
import {
  updateApplication,
  deleteApplication,
  getApplication,
  getApplicationStatusVersions,
  getApplicationStatusDates,
  getApplicationsWithStatusDates,
} from "../../api/borrowerPortal/applications.js";
import { uploadFileToApplication } from "../../api/borrowerPortal/uploadFile.js";
// Shortlisted API no longer used here; loan_amount, property_value, repayment_type come from application payload (stored at submit)
import { useNavigate, useSearchParams } from "react-router-dom";
import { api } from "../../api/client.js";
import { extractIllustrationData, generateClientIllustrationPDFWithLogo } from "../../utils/generateClientIllustration.js";
import { getBorrowerEmail } from "../../utils/auth";
import SleekUploadTile from "../../components/ui/SleekUploadTile.jsx";
import PhoneField from "../../components/ui/PhoneField.jsx";
import { applicationFormPhones, arePhonesValid } from "../../utils/validatePhone.js";
import ReviewPromptModal from "../../components/reviews/ReviewPromptModal.jsx";
import PropertyTypeChips from "../../components/ui/PropertyTypeChips.jsx";
import {
  formatProductCategoryLabel,
  resolveBorrowerProductCategory,
} from "../../utils/complexBtlProduct.js";
import {
  IF_UPLOAD_BUCKETS,
  IF_UPLOAD_FIELD_ALIASES,
  UPLOAD_FORMAT_HINT,
  normalizeIfUploads,
} from "./forms/shared/borrowerUploadConfig.js";

const BTL_DOC_FIELDNAMES = [
  "property_accounts",
  "3_months_bank_statement",
  "property_portfolio",
  "assets__liability_statement",
  "self_employed",
  "employed",
  "3_months_personal_bank_statements",
  "identification",
];

const IF_DOC_FIELDNAMES = IF_UPLOAD_BUCKETS.map((b) => b.fieldname);

const BTL_ATTACH_FIELD_MAPPING = {
  borrower_property_accounts: { bucketKey: "borrower_accounts", backendField: "property_accounts" },
  borrower_accounts: { bucketKey: "borrower_accounts", backendField: "property_accounts" },
  borrower_bank_statements: { bucketKey: "borrower_bank_statements", backendField: "3_months_bank_statement" },
  borrower_property_portfolio: { bucketKey: "borrower_portfolio", backendField: "property_portfolio" },
  borrower_portfolio: { bucketKey: "borrower_portfolio", backendField: "property_portfolio" },
  applicant_assets_liability: { bucketKey: "app_assets_liability", backendField: "assets__liability_statement" },
  app_assets_liability: { bucketKey: "app_assets_liability", backendField: "assets__liability_statement" },
  applicant_self_employed_docs: { bucketKey: "app_self_employed", backendField: "self_employed" },
  app_self_employed: { bucketKey: "app_self_employed", backendField: "self_employed" },
  applicant_employed_docs: { bucketKey: "app_employed", backendField: "employed" },
  app_employed: { bucketKey: "app_employed", backendField: "employed" },
  applicant_personal_bank_statements: { bucketKey: "app_personal_bank", backendField: "3_months_personal_bank_statements" },
  app_personal_bank: { bucketKey: "app_personal_bank", backendField: "3_months_personal_bank_statements" },
  applicant_identification: { bucketKey: "app_id", backendField: "identification" },
  app_id: { bucketKey: "app_id", backendField: "identification" },
};

function buildIfAttachFieldMapping() {
  const mapping = {};
  IF_UPLOAD_BUCKETS.forEach((bucket) => {
    mapping[bucket.fieldname] = { bucketKey: bucket.key, backendField: bucket.fieldname };
  });
  Object.entries(IF_UPLOAD_FIELD_ALIASES).forEach(([legacyKey, canonicalKey]) => {
    mapping[legacyKey] = { bucketKey: canonicalKey, backendField: canonicalKey };
  });
  // Extra aliases seen in older uploads / backend labels
  mapping.aged_debtors = { bucketKey: "if_aged_debtors", backendField: "if_aged_debtors" };
  mapping.aged_creditors = { bucketKey: "if_aged_creditors", backendField: "if_aged_creditors" };
  mapping.if_aged_debtor = { bucketKey: "if_aged_debtors", backendField: "if_aged_debtors" };
  mapping.if_aged_creditor = { bucketKey: "if_aged_creditors", backendField: "if_aged_creditors" };
  // Legacy ID uploads that were incorrectly attached to BTL fieldnames on IF apps
  mapping.applicant_identification = { bucketKey: "if_id_passport", backendField: "if_id_passport" };
  mapping.app_id = { bucketKey: "if_id_passport", backendField: "if_id_passport" };
  return mapping;
}

const IF_ATTACH_FIELD_MAPPING = buildIfAttachFieldMapping();

function splitDocFieldNames(value) {
  return String(value || "")
    .split(",")
    .map((f) => f.trim())
    .filter(Boolean);
}

function fileMatchesDocField(fileName, allowedFileNames) {
  if (!allowedFileNames?.length) return true; // empty field: trust attached_to_field (fixes legacy IF mis-maps)
  return allowedFileNames.some(
    (allowedName) =>
      fileName === allowedName ||
      fileName.includes(allowedName) ||
      allowedName.includes(fileName)
  );
}


// ----- helpers -----
const money = (v) => {
  if (v === null || v === undefined || v === "") return "—";
  if (typeof v === "string") return v;
  try {
    return new Intl.NumberFormat("en-GB", {
      style: "currency",
      currency: "GBP",
      maximumFractionDigits: 0,
    }).format(Number(v));
  } catch {
    return String(v);
  }
};

const formatDate = (dateStr) => {
  if (!dateStr || dateStr === "—") return "—";
  try {
    const date = new Date(dateStr);
    if (isNaN(date.getTime())) return dateStr;
    return date.toLocaleDateString("en-GB", { day: "2-digit", month: "short", year: "numeric" });
  } catch {
    return dateStr;
  }
};

const formatDateTime = (iso) => {
  if (!iso) return "—";
  try {
    const d = new Date(iso);
    if (isNaN(d.getTime())) return String(iso);
    const date = d.toLocaleDateString("en-GB", { day: "2-digit", month: "short", year: "numeric" });
    const time = d.toLocaleTimeString("en-GB", { hour: "2-digit", minute: "2-digit" });
    return `${date} • ${time}`;
  } catch {
    return String(iso);
  }
};

const isApplicationComplete = (status) => {
  const s = String(status || "").trim().toLowerCase();
  return s === "completion" || s === "completed" || (s.includes("completion") && !s.includes("incomplete"));
};

const fireCompletionConfetti = () => {
  if (typeof window === "undefined") return;
  if (window.matchMedia("(prefers-reduced-motion: reduce)").matches) return;

  const colors = ["#1aa0db", "#16a34a", "#f59e0b", "#8b5cf6", "#ec4899"];
  const duration = 2800;
  const end = Date.now() + duration;

  const burst = (originX) => {
    confetti({
      particleCount: 4,
      angle: originX < 0.5 ? 60 : 120,
      spread: 58,
      startVelocity: 52,
      origin: { x: originX, y: 0.55 },
      colors,
      zIndex: 10050,
    });
  };

  confetti({
    particleCount: 90,
    spread: 78,
    startVelocity: 42,
    origin: { x: 0.5, y: 0.62 },
    colors,
    zIndex: 10050,
  });

  const frame = () => {
    burst(Math.random() * 0.25 + 0.05);
    burst(Math.random() * 0.25 + 0.7);
    if (Date.now() < end) requestAnimationFrame(frame);
  };
  frame();
};

// Format time to show date and time (Date • HH:MM)
// eslint-disable-next-line no-unused-vars
const formatTime = (iso) => {
  if (!iso) return "—";
  try {
    const d = new Date(iso);
    if (isNaN(d.getTime())) return String(iso);
    const date = d.toLocaleDateString("en-GB", { day: "2-digit", month: "short", year: "numeric" });
    const time = d.toLocaleTimeString("en-GB", { hour: "2-digit", minute: "2-digit", hour12: false });
    return `${date} • ${time}`;
  } catch {
    return String(iso);
  }
};

// eslint-disable-next-line no-unused-vars
const truncate = (str, max) => {
  const s = String(str ?? "");
  if (s.length <= max) return s;
  return s.slice(0, Math.max(0, max - 3)) + "...";
};

// eslint-disable-next-line no-unused-vars
const sanitizeCurrency = (v) => {
  const s = String(v ?? "").replace(/[£,\s]/g, "");
  const num = Number(s);
  return Number.isFinite(num) ? num : 0;
};

// eslint-disable-next-line no-unused-vars
const clampNumber = (n, min, max) => {
  if (!Number.isFinite(n)) return min;
  return Math.min(max, Math.max(min, n));
};

const pickApplicantEmail = () => {
  return (
    localStorage.getItem("BORROWER_EMAIL") ||
    localStorage.getItem("borrowerEmail") ||
    localStorage.getItem("email") ||
    localStorage.getItem("userEmail") ||
    "test3@example.com"
  );
};

// Stage info text mapping for info bars
const STAGE_INFO_TEXT = {
  preenquiry: "Client has selected a product and submitted an online application for admin review.",
  enquiry: "Admin has reviewed the submission and formally logged the enquiry.",
  application: "Application details and documents are being finalised and prepared for lender submission.",
  approval: "Lender is reviewing the application and carrying out underwriting checks.",
  valuation: "Property valuation is being completed to confirm value and suitability.",
  audit: "Facility audit is being completed to confirm ledger and security details.",
  legal: "Legal checks and lender conditions are being completed prior to release of funds.",
  completion: "Funds have been released and the mortgage application is completed.",
};

function applicationPropertyChipProduct(app, extra = {}) {
  return (
    app?.productChipSource ||
    extra.productData || {
      ...app?.applicationPayload,
      product_category: app?.productCategory,
      name: app?.productId,
    }
  );
}

function ApplicationTypeWithChips({
  as: Tag = "h3",
  label,
  product,
  compact = false,
  className = "",
}) {
  return (
    <div className={`flex items-start gap-1.5 min-w-0 ${className}`}>
      <Tag className={Tag === "h1" ? "ta-title" : "font-bold text-slate-900 text-lg leading-tight"}>
        {label}
      </Tag>
      <span className="-mt-0.5 inline-flex flex-wrap items-start gap-1 shrink-0">
        <PropertyTypeChips product={product} compact={compact} omitKeys={["BTL"]} />
      </span>
    </div>
  );
}

function isInvoiceFinanceApplication(source = {}) {
  const raw = source._raw || source;
  const full = source.fullApp || {};
  const cat = String(
    source.productCategory ||
      source.product_category ||
      source.category ||
      source.productType ||
      raw.product_category ||
      raw.category ||
      raw.product_type ||
      full.product_type ||
      full.product_category ||
      ""
  )
    .trim()
    .toUpperCase();
  const purpose = String(
    source.loan_purpose || raw.loan_purpose || full.loan_purpose || ""
  ).toLowerCase();
  const id = String(
    source.id || source.name || raw.name || full.name || ""
  ).toUpperCase();
  const hasIfPayload = !!(
    source.applicationPayload?.if ||
    raw.applicationPayload?.if ||
    (typeof full.application_payload === "string" &&
      full.application_payload.includes('"if"'))
  );
  return (
    cat === "IF" ||
    cat.includes("INVOICE") ||
    purpose.includes("invoice") ||
    id.startsWith("IF-") ||
    hasIfPayload
  );
}

function formatContractTermLabel(source = {}) {
  const raw = source._raw || source;
  const months = Number(
    source.contract_term_months ??
      source.termMonths ??
      source.term_months ??
      raw.contract_term_months ??
      raw.term_months ??
      raw.termMonths
  );
  if (Number.isFinite(months) && months > 0) {
    return `${Math.round(months)} month${Math.round(months) === 1 ? "" : "s"}`;
  }
  return "—";
}

function formatIfApplicationTitle(source = {}) {
  const raw = source._raw || source;
  const facilityType = String(
    source.facility_type ?? source.facilityType ?? raw.facility_type ?? ""
  )
    .trim()
    .toLowerCase();
  let confidential = null;
  if (facilityType === "confidential") confidential = true;
  else if (facilityType === "disclosure" || facilityType === "disclosed") confidential = false;
  else {
    const flag = source.confidential_facility ?? source.confidential ?? raw.confidential_facility ?? raw.confidential;
    confidential = flag === true || flag === 1 || flag === "1";
  }
  const label = confidential ? "Confidential" : "Disclosed";
  return `${label} - ${formatContractTermLabel(source)}`;
}

function getActivityStagesForApplication(source = {}) {
  const isIf = isInvoiceFinanceApplication(source);
  return ACTIVITY_STAGES.map((stage) => {
    if (stage.key !== "valuation") return stage;
    if (!isIf) return stage;
    return {
      ...stage,
      title: "Audit",
      status: "Audit",
      // Keep valuation_* date fields for backend compatibility; also accept audit_* aliases.
    };
  });
}

function StageInfoPopup({ anchorId, title, text, onClose }) {
  const popupRef = useRef(null);
  const [popupStyle, setPopupStyle] = useState({ visibility: "hidden" });

  const updatePosition = useCallback(() => {
    const button = document.getElementById(anchorId);
    const popup = popupRef.current;
    if (!button || !popup) return;

    const btnRect = button.getBoundingClientRect();
    const popupRect = popup.getBoundingClientRect();
    const edge = 12;
    const gap = 10;
    const vw = window.innerWidth;
    const vh = window.innerHeight;
    const isMobile = vw < 768;

    if (isMobile) {
      const maxWidth = Math.min(220, vw - edge * 2);
      const popupHeight = popupRect.height || 100;

      let top = btnRect.bottom + gap;
      if (top + popupHeight > vh - edge) {
        top = btnRect.top - popupHeight - gap;
      }
      if (top < edge || top + popupHeight > vh - edge) {
        top = Math.max(edge, (vh - popupHeight) / 2);
      }

      let left = btnRect.left + btnRect.width / 2 - maxWidth / 2;
      left = Math.max(edge, Math.min(left, vw - edge - maxWidth));

      setPopupStyle({
        position: "fixed",
        left: `${left}px`,
        width: `${maxWidth}px`,
        top: `${top}px`,
        transform: "none",
        maxWidth: `${maxWidth}px`,
        minWidth: "0",
        visibility: "visible",
        zIndex: 10000,
      });
      return;
    }

    const maxWidth = 240;
    let top = btnRect.top - gap;
    let transform = "translate(-50%, -100%)";

    if (top - popupRect.height < edge) {
      top = btnRect.bottom + gap;
      transform = "translate(-50%, 0)";
    }

    if (top + popupRect.height > vh - edge && transform === "translate(-50%, 0)") {
      top = Math.max(edge, vh - popupRect.height - edge);
    }

    let left = btnRect.left + btnRect.width / 2;
    const halfWidth = Math.min(popupRect.width || maxWidth, maxWidth) / 2;
    left = Math.max(edge + halfWidth, Math.min(left, vw - edge - halfWidth));

    setPopupStyle({
      position: "fixed",
      top: `${top}px`,
      left: `${left}px`,
      transform,
      maxWidth: `${maxWidth}px`,
      minWidth: "180px",
      visibility: "visible",
      zIndex: 10000,
    });
  }, [anchorId]);

  useLayoutEffect(() => {
    updatePosition();
    const frame = requestAnimationFrame(() => updatePosition());
    window.addEventListener("scroll", updatePosition, true);
    window.addEventListener("resize", updatePosition);
    return () => {
      cancelAnimationFrame(frame);
      window.removeEventListener("scroll", updatePosition, true);
      window.removeEventListener("resize", updatePosition);
    };
  }, [updatePosition, title, text]);

  if (typeof document === "undefined") return null;

  return createPortal(
    <div
      ref={popupRef}
      className="ta-stageInfoPopup"
      style={popupStyle}
      onClick={(e) => e.stopPropagation()}
      role="dialog"
      aria-label={`${title} information`}
    >
      <div className="ta-stageInfoPopupTitle">{title}</div>
      <div className="ta-stageInfoPopupText">{text || "No information available."}</div>
      <button
        type="button"
        className="ta-stageInfoPopupClose"
        onClick={(e) => {
          e.stopPropagation();
          onClose();
        }}
        aria-label="Close"
      >
        <X className="h-4 w-4" />
      </button>
    </div>,
    document.body
  );
}

const ACTIVITY_STAGES = [
  { key: "preenquiry", title: "Pre-enquiry", status: "Pre Enquiry", dateField: "pre_enquiry_date" },
  { key: "enquiry", title: "Enquiry", status: "Enquiry", dateField: "enquiry_date" },
  { key: "application", title: "Application", status: "Application", dateField: "application_date" },
  { key: "approval", title: "Approval", status: "Approval", dateField: "approval_date" },
  { key: "valuation", title: "Valuation", status: "Valuation", dateField: "valuation_date" },
  { key: "legal", title: "Legal", status: "Legals", dateField: "legal_date" },
  { key: "completion", title: "Completion", status: "Completion", dateField: "completion_date" },
];

const STAGE_DATE_FIELD_ALIASES = {
  preenquiry: [
    "pre_enquiry_date",
    "pre_enquiry_on",
    "pre_enquiry_datetime",
    "pre_enquiry_changed_on",
    "preenquiry_changed_on",
  ],
  enquiry: ["enquiry_date", "enquiry_on", "enquiry_datetime", "enquiry_changed_on"],
  application: [
    "application_date",
    "application_on",
    "application_datetime",
    "application_submitted_date",
    "application_changed_on",
  ],
  approval: ["approval_date", "approval_on", "approval_datetime", "approval_changed_on"],
  valuation: [
    "valuation_date",
    "valuation_on",
    "valuation_datetime",
    "valuation_changed_on",
    "audit_date",
    "audit_on",
    "audit_datetime",
    "audit_changed_on",
  ],
  legal: [
    "legal_date",
    "legals_date",
    "legal_on",
    "legals_on",
    "legals_datetime",
    "legal_changed_on",
    "legals_changed_on",
  ],
  completion: [
    "completion_date",
    "completion_on",
    "completion_datetime",
    "completed_date",
    "completion_changed_on",
  ],
};

const normalizeActivityDate = (dateStr) => {
  if (!dateStr) return "";
  if (dateStr instanceof Date && !isNaN(dateStr.getTime())) {
    return dateStr.toISOString();
  }
  let clean = String(dateStr).trim();
  if (!clean) return "";
  clean = clean.replace(/\.\d+$/, "");
  if (clean.includes(" ") && !clean.includes("T")) {
    clean = clean.replace(" ", "T");
  }
  if (!clean.includes(":")) {
    clean = `${clean}T00:00:00`;
  }
  return clean;
};

const pickRawDate = (rawData, keys) => {
  for (const key of keys) {
    const val = rawData?.[key];
    if (val != null && String(val).trim() !== "") {
      return normalizeActivityDate(val);
    }
  }
  return "";
};

const getStageDateFromRaw = (rawData, stage) => {
  const statusSlug = stage.status.toLowerCase().replace(/\s+/g, "_");
  const aliases = STAGE_DATE_FIELD_ALIASES[stage.key] || [];
  return pickRawDate(rawData, [
    ...aliases,
    stage.dateField,
    `${stage.key}_date`,
    `${stage.key}Date`,
    `${stage.key}_on`,
    `${stage.key}_datetime`,
    `${statusSlug}_date`,
    `${statusSlug}_on`,
    `${statusSlug}_datetime`,
  ]);
};

const resolveCurrentStageIndex = (status, stages) => {
  const statusLower = String(status || "").trim().toLowerCase();
  let currentStageIndex = -1;
  stages.forEach((stage, idx) => {
    let isMatch = false;
    if (stage.key === "preenquiry") {
      isMatch = status === "Pre Enquiry" || statusLower.includes("pre enquiry") || statusLower.includes("pre-enquiry");
    } else if (stage.key === "enquiry") {
      isMatch = (status === "Enquiry" || statusLower.includes("enquiry")) && !statusLower.includes("pre");
    } else if (stage.key === "legal") {
      isMatch = statusLower.includes("legal");
    } else if (stage.key === "valuation") {
      // IF apps use "Audit"; BTL keeps "Valuation". Match either status string.
      isMatch =
        status === stage.status ||
        statusLower.includes("valuation") ||
        statusLower.includes("audit") ||
        status === "Valuation" ||
        status === "Audit";
    } else {
      isMatch = status === stage.status || statusLower.includes(stage.key);
    }
    if (isMatch) currentStageIndex = idx;
  });
  return currentStageIndex;
};

/** IF: Valuation stage is shown as Audit. Prefer live application status for Current Stage. */
const resolveDisplayCurrentStage = (status, currentStage, isIfApp = false) => {
  const primary = String(status || currentStage || "").trim();
  if (!primary) return "—";
  const lower = primary.toLowerCase();
  if (isIfApp && (lower.includes("audit") || lower.includes("valuation"))) return "Audit";
  // Also catch Audit even if IF detection missed
  if (lower === "audit" || lower.includes("audit")) return "Audit";
  return primary;
};

const HISTORY_TABLE_KEYS = [
  "application_status_history",
  "application_activity",
  "application_activities",
  "activity_history",
  "status_history",
  "stage_history",
  "application_timeline",
];

const HISTORY_ROW_DATE_KEYS = [
  "status_changed_on",
  "changed_on",
  "changed_at",
  "datetime",
  "timestamp",
  "date",
  "stage_date",
  "modified",
  "creation",
];

const HISTORY_ROW_STAGE_KEYS = [
  "stage",
  "status",
  "activity",
  "activity_stage",
  "stage_name",
  "application_status",
];

const matchStageKeyFromLabel = (label) => {
  const s = String(label || "").trim().toLowerCase();
  if (!s) return null;

  for (const stage of ACTIVITY_STAGES) {
    if (s === stage.status.toLowerCase()) return stage.key;
    if (s === stage.key) return stage.key;
    if (stage.key === "preenquiry" && (s.includes("pre enquiry") || s.includes("pre-enquiry"))) return stage.key;
    if (stage.key === "enquiry" && s.includes("enquiry") && !s.includes("pre")) return stage.key;
    if (stage.key === "legal" && s.includes("legal")) return stage.key;
    if (stage.key === "valuation" && (s.includes("valuation") || s.includes("audit"))) return stage.key;
    if (s.includes(stage.key)) return stage.key;
  }
  return null;
};

const getStageDatesFromHistoryTables = (rawData) => {
  const dates = {};

  for (const tableKey of HISTORY_TABLE_KEYS) {
    const rows = rawData?.[tableKey];
    if (!Array.isArray(rows)) continue;

    for (const row of rows) {
      let stageKey = null;
      for (const field of HISTORY_ROW_STAGE_KEYS) {
        stageKey = matchStageKeyFromLabel(row?.[field]);
        if (stageKey) break;
      }
      if (!stageKey) continue;

      const dt = pickRawDate(row, HISTORY_ROW_DATE_KEYS);
      if (!dt) continue;

      const existing = dates[stageKey];
      if (!existing || new Date(dt).getTime() > new Date(existing).getTime()) {
        dates[stageKey] = dt;
      }
    }
  }

  return dates;
};

const STATUS_DATES_NESTED_KEYS = ["stage_dates", "status_dates", "stages", "dates", "history", "timeline", "data"];

const isDateLike = (val) =>
  val != null && typeof val !== "object" && String(val).trim() !== "" && !isNaN(Date.parse(normalizeActivityDate(val)));

/**
 * Parse get_application_status_dates (or one row of get_applications) into { stageKey: datetime }.
 * Accepts { Enquiry: "...", ... }, { enquiry_date: "..." }, [{ stage, date }], or those nested under a known key.
 */
const parseApiStageDates = (payload) => {
  const dates = {};
  if (!payload || typeof payload !== "object") return dates;

  const assign = (label, val) => {
    if (!isDateLike(val)) return;
    const stageKey = matchStageKeyFromLabel(
      String(label || "").replace(/_(date|datetime|on|at|changed_on)$/i, "").replace(/_/g, " ")
    );
    if (stageKey && !dates[stageKey]) dates[stageKey] = normalizeActivityDate(val);
  };

  if (Array.isArray(payload)) {
    for (const row of payload) {
      if (!row || typeof row !== "object") continue;
      const label = HISTORY_ROW_STAGE_KEYS.map((k) => row[k]).find(Boolean);
      const dt = pickRawDate(row, HISTORY_ROW_DATE_KEYS);
      if (label && dt) assign(label, dt);
    }
    return dates;
  }

  for (const nestedKey of STATUS_DATES_NESTED_KEYS) {
    const nested = payload[nestedKey];
    if (!nested || typeof nested !== "object") continue;
    for (const [stageKey, dt] of Object.entries(parseApiStageDates(nested))) {
      if (!dates[stageKey]) dates[stageKey] = dt;
    }
  }

  for (const [key, val] of Object.entries(payload)) {
    const k = key.toLowerCase();
    if (k === "creation" || k === "modified" || k === "name") continue;
    if (val && typeof val === "object" && !Array.isArray(val)) {
      assign(key, pickRawDate(val, HISTORY_ROW_DATE_KEYS));
    } else {
      assign(key, val);
    }
  }

  return dates;
};

const parseStageDatesFromVersions = (versions, rawData = {}) => {
  const dates = {};
  const created =
    rawData.creation || rawData.submitted_on || rawData.submitted_date || rawData.date_time;
  if (created) {
    dates.preenquiry = normalizeActivityDate(created);
  }

  const assignStageDate = (statusValue, at, { overwrite = false } = {}) => {
    const stageKey = matchStageKeyFromLabel(statusValue);
    if (!stageKey || !at) return;
    if (!overwrite && dates[stageKey]) return;
    dates[stageKey] = at;
  };

  const sorted = [...(versions || [])].sort(
    (a, b) => new Date(a.creation || a.modified).getTime() - new Date(b.creation || b.modified).getTime()
  );

  for (const ver of sorted) {
    let data;
    try {
      data = typeof ver.data === "string" ? JSON.parse(ver.data) : ver.data;
    } catch {
      continue;
    }
    if (!data || typeof data !== "object") continue;

    const at = normalizeActivityDate(ver.creation || ver.modified);
    if (!at) continue;

    for (const item of data?.added || []) {
      if (!Array.isArray(item)) continue;
      const field = String(item[0] || "").toLowerCase();
      if (field === "status") assignStageDate(item[1], at);
    }
    for (const item of data?.changed || []) {
      if (!Array.isArray(item)) continue;
      const field = String(item[0] || "").toLowerCase();
      if (field === "status") {
        // New status value is index 2: [field, old, new]
        assignStageDate(item[2], at);
      }
      // When only status_changed_on updates in a version, map that time to the
      // status value from the same version payload if present.
      if (field === "status_changed_on" || field === "status_changed_at") {
        const statusChange = (data.changed || []).find(
          (row) => Array.isArray(row) && String(row[0] || "").toLowerCase() === "status"
        );
        if (statusChange) assignStageDate(statusChange[2], at, { overwrite: true });
      }
    }
  }

  return dates;
};

const buildStageDateMap = (rawData, versionStageDates = {}, apiStageDates = {}) => {
  const dates = { ...versionStageDates, ...getStageDatesFromHistoryTables(rawData) };

  ACTIVITY_STAGES.forEach((stage) => {
    const direct = getStageDateFromRaw(rawData, stage);
    if (direct) dates[stage.key] = direct;
  });

  Object.assign(dates, apiStageDates);

  if (!rawData || typeof rawData !== "object") return dates;

  // Match any remaining *_date / *_on / *_changed_on fields on the application doc itself.
  for (const [key, val] of Object.entries(rawData)) {
    if (val == null || typeof val === "object") continue;
    const k = key.toLowerCase();
    if (!k.includes("date") && !k.includes("_on") && !k.includes("datetime") && !k.includes("changed")) {
      continue;
    }
    if (
      k === "modified" ||
      k === "modified_on" ||
      k === "creation" ||
      k === "submitted_on" ||
      k === "submitted_date" ||
      k === "date_time" ||
      k === "datetime" ||
      k === "status_changed_on" ||
      k === "status_changed_at"
    ) {
      continue;
    }
    if (k === "stage_start_date" || k === "stage_start_on" || k === "stage_start_datetime") {
      continue;
    }

    for (const stage of ACTIVITY_STAGES) {
      if (dates[stage.key]) continue;
      const statusPart = stage.status.toLowerCase().replace(/\s+/g, "_");
      const keyCompact = stage.key === "preenquiry" ? "pre_enquiry" : stage.key;
      if (k.includes(keyCompact) || k.includes(stage.key) || k.includes(statusPart)) {
        const normalized = normalizeActivityDate(val);
        if (normalized) dates[stage.key] = normalized;
      }
    }
  }

  return dates;
};

// Generate activity timeline based on application status.
// Each reached stage shows its own backend date/time when available.
const getActivities = (app, rawData = {}, versionStageDates = {}, apiStageDates = {}) => {
  const status = (app.status || "").trim();
  const stages = getActivityStagesForApplication({ ...app, ...rawData, _raw: rawData });
  const currentStageIndex = resolveCurrentStageIndex(status, stages);
  const stageDateMap = buildStageDateMap(rawData, versionStageDates, apiStageDates);

  const submittedOn =
    app.submitted_on ||
    app.submittedDate ||
    rawData.submitted_on ||
    rawData.submitted_date ||
    rawData.creation ||
    rawData.date_time ||
    "";
  const modifiedOn = app.modified || app.modified_on || rawData.modified || rawData.modified_on || "";
  const submissionDateTime = submittedOn ? normalizeActivityDate(submittedOn) : "";
  const modifiedDateTime = modifiedOn ? normalizeActivityDate(modifiedOn) : "";
  const stageStartDate = pickRawDate(rawData, ["stage_start_date", "stage_start_on", "stage_start_datetime"]);
  const statusChangedOn = pickRawDate(rawData, ["status_changed_on", "status_changed_at"]);

  const activities = stages.map((stage, idx) => {
    let activityStatus = "pending";
    let date = stageDateMap[stage.key] || "";

    if (currentStageIndex > -1) {
      if (idx < currentStageIndex) {
        activityStatus = "completed";
        if (!date && idx === 0 && submissionDateTime) {
          date = submissionDateTime;
        }
      } else if (idx === currentStageIndex) {
        activityStatus = "inprogress";
        if (!date) {
          date =
            statusChangedOn ||
            stageStartDate ||
            modifiedDateTime ||
            (idx === 0 ? submissionDateTime : "");
        }
      }
    } else if (submissionDateTime && idx === 0) {
      activityStatus = "inprogress";
      if (!date) date = submissionDateTime;
    }

    return {
      key: stage.key,
      title: stage.title,
      date,
      status: activityStatus,
    };
  });

  // When status jumps ahead, earlier completed stages may lack their own field.
  // Backfill from the next known stage timestamp so every reached stage shows a time.
  if (currentStageIndex > 0) {
    for (let i = currentStageIndex - 1; i >= 0; i -= 1) {
      if (activities[i].date) continue;
      for (let j = i + 1; j <= currentStageIndex; j += 1) {
        if (activities[j].date) {
          activities[i].date = activities[j].date;
          break;
        }
      }
      if (!activities[i].date && statusChangedOn) {
        activities[i].date = statusChangedOn;
      }
    }
  }

  return activities;
};

const mergeApplicationWithBackend = (app, fullApp, versions = [], statusDatesPayload = null) => {
  if (!fullApp) return app;
  const updatedRaw = { ...(app._raw || {}), ...fullApp };
  const rawStatus = fullApp.status || fullApp.application_status || fullApp.stage || app.status;
  const isIf = isInvoiceFinanceApplication({
    ...app,
    ...fullApp,
    _raw: updatedRaw,
    productCategory: app.productCategory || fullApp.product_category || fullApp.product_type,
    product_category: fullApp.product_category || app.product_category,
  });
  const status =
    isIf && /valuation|audit/i.test(String(rawStatus || ""))
      ? "Audit"
      : rawStatus;
  const submittedOn =
    fullApp.submitted_on ||
    fullApp.submitted_date ||
    fullApp.creation ||
    app.submitted_on ||
    "";
  const modifiedOn = fullApp.modified || fullApp.modified_on || app._raw?.modified || "";
  const versionStageDates = parseStageDatesFromVersions(versions, updatedRaw);
  const apiStageDates = statusDatesPayload
    ? parseApiStageDates(statusDatesPayload)
    : app._apiStageDates || {};
  const currentStage = resolveDisplayCurrentStage(
    status,
    fullApp.current_stage || fullApp.stage || fullApp.currentStage || app.currentStage,
    isIf
  );

  return {
    ...app,
    status,
    currentStage,
    submitted_on: submittedOn,
    _raw: updatedRaw,
    _apiStageDates: apiStageDates,
    activity: getActivities(
      {
        status,
        productType: isIf ? "IF" : app.productType,
        product_category: isIf ? "IF" : app.product_category || app.productCategory,
        submitted_on: submittedOn,
        submittedDate: app.submittedDate,
        modified: modifiedOn,
      },
      updatedRaw,
      versionStageDates,
      apiStageDates
    ),
  };
};

// Helper function to fetch product by ID
async function getProductById(productId) {
  if (!productId) return null;
  try {
    const encoded = encodeURIComponent(productId);
    const res = await api.get(`/api/resource/Mortgage Product/${encoded}`);
    return res?.data?.data || res?.data || null;
  } catch (error) {
    console.warn("Failed to fetch product:", productId, error);
    return null;
  }
}

export default function TrackApplications() {
  // eslint-disable-next-line no-unused-vars
  const navigate = useNavigate();
  const [searchParams, setSearchParams] = useSearchParams();
  const [selectedApplication, setSelectedApplication] = useState(null);
  const [apps, setApps] = useState([]);
  const [loading, setLoading] = useState(true);
  const [err, setErr] = useState("");
  const applicantEmail = useMemo(() => pickApplicantEmail(), []);
  const [postSubmitReviewOpen, setPostSubmitReviewOpen] = useState(false);

  useEffect(() => {
    if (searchParams.get("submitted") !== "1") return;
    setPostSubmitReviewOpen(true);
    const next = new URLSearchParams(searchParams);
    next.delete("submitted");
    setSearchParams(next, { replace: true });
  }, [searchParams, setSearchParams]);

  // Open a specific application from notification "View details" (?app=ID)
  useEffect(() => {
    const appId = String(searchParams.get("app") || "").trim();
    if (!appId || loading) return;

    const match = apps.find((a) => String(a.id) === appId);
    if (match) {
      setSelectedApplication(match.id);
      const next = new URLSearchParams(searchParams);
      next.delete("app");
      setSearchParams(next, { replace: true });
      return;
    }

    if (apps.length > 0) {
      const next = new URLSearchParams(searchParams);
      next.delete("app");
      setSearchParams(next, { replace: true });
    }
  }, [searchParams, setSearchParams, apps, loading]);

  const [toast, setToast] = useState(null);
  const [deleteOpen, setDeleteOpen] = useState(false);
  const [deleteConfirm, setDeleteConfirm] = useState("");
  const [isDeleting, setIsDeleting] = useState(false);

  // Store product data for selected application
  const [productData, setProductData] = useState(null);
  const [loadingProduct, setLoadingProduct] = useState(false);

  // Edit modal state
  const [editModalOpen, setEditModalOpen] = useState(false);
  const [editingApplication, setEditingApplication] = useState(null);
  // eslint-disable-next-line no-unused-vars
  const [isSaving, setIsSaving] = useState(false);

  // Store product data for all applications (keyed by productId)
  const [productsMap, setProductsMap] = useState({});

  // Prevent multiple PDF generations
  const isGeneratingPDF = useRef(false);

  // Refresh state
  const [isRefreshing, setIsRefreshing] = useState(false);
  const [activeInfoPopup, setActiveInfoPopup] = useState(null);
  const [completionCongratsOpen, setCompletionCongratsOpen] = useState(false);

  useEffect(() => {
    setActiveInfoPopup(null);
  }, [selectedApplication]);
  const completionCelebratedRef = useRef(null);

  // ---- LOAD FROM BACKEND ----
  const load = async (silent = false) => {
    if (!silent) {
      setLoading(true);
    }
    setErr("");
    try {
      const [rows, statusRows] = await Promise.all([
        getTrackApplicationsByApplicant(applicantEmail, {
          limit: 100,
          orderBy: "modified desc",
          fields: ["*"],
        }),
        getApplicationsWithStatusDates(),
      ]);

      const apiStageDatesById = {};
      for (const row of statusRows || []) {
        const appName = row?.name || row?.application || row?.application_name || row?.application_id;
        if (appName) apiStageDatesById[appName] = parseApiStageDates(row);
      }

      // Debug: Log the raw API response to see structure
      console.log("[TrackApplications] Raw API response rows:", rows);
      if (rows && rows.length > 0) {
        console.log("[TrackApplications] First row sample:", rows[0]);
        console.log("[TrackApplications] First row loan_amount:", rows[0].loan_amount, typeof rows[0].loan_amount);
        console.log("[TrackApplications] First row product:", rows[0].product);
      }

      // Normalize for your UI (safe fallbacks if fields differ)
      const normalized = (rows || []).map((r) => {
        const id = r.name || r.id || r.application_id || "—";
        const productType = r.product_type || r.productType || r.loan_type || "Application";

        // Parse application_payload first to get potential product ID and loan amount
        let payload = null;
        let payloadProductId = null;
        try {
          if (r.application_payload) {
            payload = typeof r.application_payload === "string"
              ? JSON.parse(r.application_payload)
              : r.application_payload;
            payloadProductId = payload?.productId || payload?.product_id ||
              payload?.application_payload?.productId || payload?.application_payload?.product_id;
          }
        } catch (e) {
          // Ignore parse errors
        }

        // Extract property address from multiple sources
        const propertyAddress = r.property_address || r.propertyAddress || r.address ||
          payload?.propertyAddress || payload?.property_address ||
          payload?.formData?.propertyAddress || payload?.formData?.property_address ||
          payload?.btl?.solo?.propertyAddress || payload?.btl?.partnership?.propertyAddress ||
          payload?.btl?.limited?.propertyAddress || payload?.btl?.pension?.propertyAddress || "";

        // Get loan_amount, property_value, repayment_type from application only (no shortlisted API).
        // These are stored in application_payload at submit time from the shortlisted product.
        const safeNum = (v, fallback = 0) => {
          if (v === null || v === undefined || v === "") return fallback;
          if (typeof v === "string") {
            const s = v.replace(/[£,%\s]/g, "");
            const n = Number(s);
            return Number.isFinite(n) ? n : fallback;
          }
          const n = Number(v);
          return Number.isFinite(n) ? n : fallback;
        };

        let loanAmountRaw = safeNum(
          r.loan_amount ?? r.mortgage_amount ?? r.amount ?? r.loanAmount ?? r.mortgageAmount ?? 0
        );
        let propertyValueRaw = safeNum(
          r.property_value ?? r.propertyValue ?? r.property_value ?? 0
        );

        if (payload) {
          if (loanAmountRaw === 0) {
            loanAmountRaw = safeNum(
              payload?.loan_amount ||
              payload?.mortgageAmount ||
              payload?.mortgage_amount ||
              payload?.loanAmount ||
              payload?.formData?.loan_amount ||
              payload?.fullPayload?.loan_amount ||
              payload?.btl?.solo?.loan_amount ||
              payload?.btl?.limited?.loan_amount ||
              payload?.btl?.partnership?.loan_amount ||
              payload?.btl?.pension?.loan_amount ||
              0
            );
          }
          if (propertyValueRaw === 0) {
            propertyValueRaw = safeNum(
              payload?.property_value ||
              payload?.propertyValue ||
              payload?.formData?.property_value ||
              payload?.fullPayload?.property_value ||
              0
            );
          }
        }
        const status = r.status || r.application_status || r.stage || "in-progress";

        // Get submitted_on (full ISO timestamp) or fall back to submitted_date/creation
        const submittedOn = r.submitted_on || r.submitted_date || r.creation || r.submittedDate || "";
        const submittedDate = submittedOn ? String(submittedOn).slice(0, 10) : "";
        const currentStage = r.current_stage || r.stage || r.currentStage || status;

        // Get loan purpose to calculate estimated completion
        const loanPurpose = r.loan_purpose || "";

        // Calculate estimated completion based on loan purpose
        // Purchase: 8 weeks, Remortgage: 6 weeks, Product transfer: 3 weeks
        let estimatedClosingDate = r.estimated_closing_date || r.estimatedClosingDate || r.expected_completion || "";
        if (!estimatedClosingDate && submittedOn) {
          const submittedDateObj = new Date(submittedOn);
          let weeksToAdd = 8; // Default to Purchase (8 weeks)
          if (loanPurpose.toLowerCase().includes("remortgage")) {
            weeksToAdd = 6;
          } else if (loanPurpose.toLowerCase().includes("product transfer") || loanPurpose.toLowerCase().includes("transfer")) {
            weeksToAdd = 3;
          }
          const estimatedDate = new Date(submittedDateObj);
          estimatedDate.setDate(estimatedDate.getDate() + (weeksToAdd * 7));
          estimatedClosingDate = estimatedDate.toISOString().slice(0, 10);
        }

        const loanOfficer =
          r.loan_officer ||
          r.case_manager ||
          r.team_member ||
          r.loanOfficer ||
          "Case Manager";
        const email =
          r.case_email ||
          r.officer_email ||
          r.team_member_email ||
          r.email ||
          "contact@example.com";
        const phone = r.case_phone || r.officer_phone || r.phone || "";

        // progress: if backend has it and it's > 0, use it; else compute a rough value by status
        let progress = Number(r.progress ?? r.progress_percent ?? "");
        const hasBackendProgress = Number.isFinite(progress) && progress > 0;

        if (!hasBackendProgress) {
          // Normalize status for matching (case-insensitive, handle variations)
          const s = String(status || "").trim().toLowerCase();

          // Match status with case-insensitive and flexible matching
          // Check more specific matches first, then general ones
          if ((s.includes("pre") || s.includes("pre-")) && s.includes("enquiry")) {
            progress = 5;
          } else if (s === "enquiry" || (s.includes("enquiry") && !s.includes("pre"))) {
            progress = 15;
          } else if (s === "application" || s.includes("application")) {
            progress = 30;
          } else if (s === "approval" || s.includes("approval")) {
            progress = 50;
          } else if (s === "valuation" || s.includes("valuation") || s === "audit" || s.includes("audit")) {
            progress = 65;
          } else if (s === "legals" || s === "legal" || s.includes("legal")) {
            progress = 80;
          } else if (s === "completion" || s.includes("completion") || s.includes("complete")) {
            progress = 95;
          } else if (s === "submitted" || s.includes("submitted")) {
            progress = 10;
          } else if (s === "pending" || s.includes("pending")) {
            progress = 5;
          } else if (s === "in-progress" || s.includes("in progress") || s === "inprogress") {
            progress = 40;
          } else {
            // Default progress based on whether status exists
            // If status exists but doesn't match, assign a default based on common patterns
            if (status && status.trim().length > 0) {
              // If status exists but doesn't match known patterns, use a default
              progress = 25; // Default for unknown statuses
            } else {
              progress = 0; // No status at all
            }
          }

          // Log for debugging
          console.log(`[TrackApplications] Calculated progress for ${id}: status="${status}" (normalized: "${s}") -> progress=${progress}%`);
        } else {
          console.log(`[TrackApplications] Using backend progress for ${id}: ${progress}%`);
        }

        // Ensure progress is always a valid number between 0 and 100
        progress = Math.max(0, Math.min(100, progress));

        // Final safety check: if status exists but progress is still 0, set a minimum
        if (progress === 0 && status && status.trim().length > 0) {
          progress = 5; // Minimum progress for any status
          console.log(`[TrackApplications] Progress was 0 but status exists, setting minimum progress to 5% for ${id}`);
        }

        // Parse application_payload to get borrower type and form data
        let borrowerType = null;
        let ownershipType = r.ownership_type || "";
        if (ownershipType === "Sole Proprietorship") borrowerType = "individual";
        else if (ownershipType === "Partnership") borrowerType = "partnership";
        else if (ownershipType === "Pvt Ltd") borrowerType = "limited";
        else if (ownershipType === "Pension/Trust") borrowerType = "pension";

        // Try to get from application_payload if available
        let applicationPayload = null;
        try {
          if (r.application_payload) {
            applicationPayload = typeof r.application_payload === "string"
              ? JSON.parse(r.application_payload)
              : r.application_payload;
            if (applicationPayload?.meta?.borrowerType) {
              borrowerType = applicationPayload.meta.borrowerType;
            }
          }
        } catch (e) {
          console.warn("Failed to parse application_payload:", e);
        }

        // Generate initials from name
        const initials = loanOfficer && loanOfficer !== "Case Manager" && loanOfficer !== "—"
          ? loanOfficer.split(" ").map(n => n[0]).join("").toUpperCase().slice(0, 2)
          : "CM";

        // Final validation before storing
        const finalLoanAmountRaw = loanAmountRaw > 0 ? Number(loanAmountRaw) : 0;
        const finalPropertyValueRaw = propertyValueRaw > 0 ? Number(propertyValueRaw) : 0;
        console.log(`[TrackApplications] Final values for ${id}:`, {
          loanAmountRaw,
          finalLoanAmountRaw,
          formatted: money(finalLoanAmountRaw),
          propertyValueRaw,
          finalPropertyValueRaw,
          formattedPropertyValue: money(finalPropertyValueRaw)
        });

        const isIfApp = isInvoiceFinanceApplication({
          ...r,
          productType,
          loan_purpose: loanPurpose,
          product_category: r.product_category || r.category || payload?.productType,
        });
        const contractTermLabel = formatContractTermLabel({
          ...r,
          contract_term_months:
            r.contract_term_months ??
            payload?.contract_term_months ??
            payload?.termMonths ??
            payload?.term_months,
        });
        const ifTitle = isIfApp
          ? formatIfApplicationTitle({
              ...r,
              facility_type: r.facility_type ?? payload?.facility_type,
              confidential_facility:
                r.confidential_facility ?? payload?.confidential_facility,
              confidential: r.confidential ?? payload?.confidential,
              disclosure: r.disclosure ?? payload?.disclosure,
              contract_term_months:
                r.contract_term_months ??
                payload?.contract_term_months ??
                payload?.termMonths ??
                payload?.term_months,
            })
          : null;
        const productCategoryKey = isIfApp
          ? "IF"
          : resolveBorrowerProductCategory({
              ...r,
              ...payload,
              product_type: productType,
              productType: payload?.productType || productType,
              product_category:
                r.product_category ||
                payload?.product_category ||
                payload?.productType ||
                productType,
              name: r.product || payload?.productId || r.mortgage_product || r.product_id,
              mortgage_product: r.product || payload?.productId || r.mortgage_product,
              applicationPayload: payload,
            });
        const displayProductLabel = isIfApp
          ? ifTitle
          : formatProductCategoryLabel(productCategoryKey);
        const displayCurrentStage = resolveDisplayCurrentStage(status, currentStage, isIfApp);

        return {
          id,
          title: isIfApp
            ? ifTitle
            : `${formatProductCategoryLabel(productCategoryKey)} Mortgage`,
          productType: displayProductLabel,
          productCategory: isIfApp ? "IF" : productCategoryKey,
          contractTermLabel: isIfApp ? contractTermLabel : null,
          propertyAddress,
          loanAmount: money(finalLoanAmountRaw),
          loanAmountRaw: finalLoanAmountRaw,
          facilityRequiredRaw: isIfApp
            ? safeNum(
                r.facility_required ??
                  payload?.facility_required ??
                  payload?.facilityRequired ??
                  r.loan_amount ??
                  finalLoanAmountRaw
              )
            : null,
          propertyValueRaw: finalPropertyValueRaw,
          status: isIfApp && /valuation|audit/i.test(String(status || "")) ? "Audit" : status,
          submittedDate: submittedDate ? String(submittedDate).slice(0, 10) : "",
          submitted: submittedDate ? String(submittedDate).slice(0, 10) : "",
          submitted_on: submittedOn, // Store full ISO timestamp for timeline
          currentStage: displayCurrentStage,
          progressPct: progress,
          progress,
          loan_purpose: loanPurpose, // Store loan purpose for calculations
          estimatedClosingDate: estimatedClosingDate ? String(estimatedClosingDate).slice(0, 10) : "",
          estCompletion: estimatedClosingDate ? String(estimatedClosingDate).slice(0, 10) : "",
          caseManager: {
            name: loanOfficer,
            email,
            phone,
            initials,
          },
          activity: getActivities({
            status,
            submittedDate: submittedDate ? String(submittedDate).slice(0, 10) : "",
            submitted_on: submittedOn, // Pass full timestamp to timeline
            modified: r.modified || r.modified_on || "", // Pass modification timestamp
            productType: isIfApp ? "IF" : productCategoryKey,
            product_category: isIfApp ? "IF" : productCategoryKey,
            loan_purpose: loanPurpose,
          }, r, {}, apiStageDatesById[id] || {}), // Pass raw backend data to access stage date fields
          _apiStageDates: apiStageDatesById[id] || {},
          borrowerType,
          applicationPayload,
          ownershipType,
          productId: r.product || r.product_id || payloadProductId || "", // Store product ID for fetching
          _raw: r,
        };
      });

      setApps(normalized);

      // Fetch product data for all unique product IDs
      const uniqueProductIds = [...new Set(normalized.map(app => app.productId).filter(Boolean))];
      if (uniqueProductIds.length > 0) {
        const productPromises = uniqueProductIds.map(productId =>
          getProductById(productId).then(product => ({ productId, product })).catch(() => ({ productId, product: null }))
        );

        Promise.all(productPromises).then(results => {
          const productsMap = {};
          results.forEach(({ productId, product }) => {
            if (product) {
              productsMap[productId] = product;
            }
          });
          setProductsMap(productsMap);

          // Update loan amounts in apps based on product data
          // Only update if the app doesn't already have a valid loan amount
          setApps(prevApps => prevApps.map(app => {
            const product = productsMap[app.productId];
            if (!product) return app;

            const nextCategory =
              app.productCategory === "IF"
                ? "IF"
                : resolveBorrowerProductCategory({
                    ...app,
                    ...product,
                    product_category: product.product_category || product.category || app.productCategory,
                    name: product.name || app.productId,
                    mortgage_product: product.name || app.productId,
                  });
            const isCbtl = nextCategory === "CBTL";
            const loanAmountFromProduct =
              product.loan_amount_full_details ||
              product.loan_amount_full ||
              product.mortgage_amount ||
              product.loan_amount ||
              product.amount ||
              product.principal_amount ||
              product.borrowing_amount ||
              product.loanAmount ||
              product.mortgageAmount ||
              null;
            const shouldFillLoan =
              !(app.loanAmountRaw && app.loanAmountRaw > 0) &&
              loanAmountFromProduct &&
              Number(loanAmountFromProduct) > 0;

            return {
              ...app,
              productChipSource: {
                ...product,
                product_category: nextCategory,
                name: product.name || app.productId,
              },
              ...(isCbtl
                ? {
                    productCategory: "CBTL",
                    productType: formatProductCategoryLabel("CBTL"),
                    title: "Complex BTL Mortgage",
                  }
                : {}),
              ...(shouldFillLoan
                ? {
                    loanAmount: money(loanAmountFromProduct),
                    loanAmountRaw: Number(loanAmountFromProduct) || 0,
                  }
                : {}),
            };
          }));
        });
      }
    } catch (e) {
      setErr(e?.message || "Failed to load applications.");
      setApps([]);
    } finally {
      if (!silent) {
        setLoading(false);
      }
    }
  };

  // 🔐 Auth readiness (matches Dashboard/Analytics logic)
  const [authReady, setAuthReady] = React.useState(false);
  React.useEffect(() => {
    const hasAuth =
      localStorage.getItem("ds_isAuthed") === "1" ||
      sessionStorage.getItem("ds_isAuthed") === "1";
    if (hasAuth) {
      const t = setTimeout(() => setAuthReady(true), 100);
      return () => clearTimeout(t);
    }
  }, []);

  // Initial load
  useEffect(() => {
    if (!authReady) return;
    load();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [authReady]);

  // Auto-refresh removed - applications are now only loaded manually

  // Update selected application when apps change (to reflect status/progress updates)
  useEffect(() => {
    if (selectedApplication && apps.length > 0) {
      const updated = apps.find((a) => a.id === selectedApplication);
      if (updated) {
        // The selected application data will be automatically updated since we're using apps.find()
        // This effect ensures the component re-renders with new data
        console.log("[TrackApplications] Selected application data updated:", {
          id: updated.id,
          status: updated.status,
          progress: updated.progressPct
        });
      }
    }
  }, [apps, selectedApplication]);

  // Refresh full application from backend when detail view opens (stage timestamps)
  useEffect(() => {
    if (!selectedApplication) return;

    let cancelled = false;
    (async () => {
      try {
        const [fullApp, versions, statusDates] = await Promise.all([
          getApplication(selectedApplication),
          getApplicationStatusVersions(selectedApplication),
          getApplicationStatusDates(selectedApplication),
        ]);
        if (cancelled || !fullApp) return;

        setApps((prev) =>
          prev.map((app) =>
            app.id === selectedApplication
              ? mergeApplicationWithBackend(app, fullApp, versions, statusDates)
              : app
          )
        );
      } catch (error) {
        console.warn("[TrackApplications] Failed to refresh application activity dates:", error);
      }
    })();

    return () => {
      cancelled = true;
    };
  }, [selectedApplication]);

  // Celebrate when the opened application reaches Completion
  useEffect(() => {
    if (!selectedApplication) {
      completionCelebratedRef.current = null;
      setCompletionCongratsOpen(false);
      return;
    }

    const app = apps.find((a) => a.id === selectedApplication);
    if (!app || !isApplicationComplete(app.status)) return;
    if (completionCelebratedRef.current === selectedApplication) return;

    completionCelebratedRef.current = selectedApplication;
    setCompletionCongratsOpen(true);
    fireCompletionConfetti();
  }, [selectedApplication, apps]);

  // Manual refresh function
  const handleRefresh = async () => {
    setIsRefreshing(true);
    try {
      await load();
      showToast("success", "Applications refreshed");
    } catch (error) {
      console.error("Failed to refresh:", error);
      showToast("error", "Failed to refresh applications");
    } finally {
      setIsRefreshing(false);
    }
  };

  // Fetch product data when an application is selected (use cached if available)
  useEffect(() => {
    if (!selectedApplication) {
      setProductData(null);
      return;
    }

    const selected = apps.find((a) => a.id === selectedApplication);
    if (!selected || !selected.productId) {
      setProductData(null);
      return;
    }

    // Check if product is already in cache
    if (productsMap[selected.productId]) {
      setProductData(productsMap[selected.productId]);
      return;
    }

    // Otherwise fetch it
    setLoadingProduct(true);
    getProductById(selected.productId)
      .then((product) => {
        setProductData(product);
        // Cache it
        if (product) {
          setProductsMap(prev => ({ ...prev, [selected.productId]: product }));
        }
      })
      .catch((error) => {
        console.error("Failed to fetch product:", error);
        setProductData(null);
      })
      .finally(() => {
        setLoadingProduct(false);
      });
  }, [selectedApplication, apps, productsMap]);

  function showToast(type, message) {
    setToast({ type, message });
    window.clearTimeout(showToast._t);
    showToast._t = window.setTimeout(() => setToast(null), 2500);
  }

  async function openEdit(app) {
    try {
      // Fetch full application data from backend
      const fullApp = await getApplication(app.id);

      // Parse application payload
      let applicationPayload = app.applicationPayload;
      if (!applicationPayload && fullApp?.application_payload) {
        applicationPayload = typeof fullApp.application_payload === "string"
          ? JSON.parse(fullApp.application_payload)
          : fullApp.application_payload;
      }

      // If applicationPayload is still empty, try to get it from app
      if (!applicationPayload || Object.keys(applicationPayload).length === 0) {
        applicationPayload = app.applicationPayload || app.payloadObj || {};
      }

      // Try to fetch attached files from backend if available
      // Frappe stores attached files, but for now we rely on application_payload
      // Log the structure for debugging
      console.log("[openEdit] Application data:", {
        id: app.id,
        hasApplicationPayload: !!applicationPayload,
        hasBtl: !!applicationPayload?.btl,
        fullAppKeys: fullApp ? Object.keys(fullApp) : [],
        applicationPayloadKeys: applicationPayload ? Object.keys(applicationPayload) : [],
      });

      // Merge backend fields into applicationPayload to ensure they are available for initialization
      // Also check app.propertyAddress which is extracted during normalization
      const mergedPayload = {
        ...(applicationPayload || {}),
        property_address: fullApp?.property_address || 
          app?.propertyAddress || 
          applicationPayload?.property_address || 
          applicationPayload?.formData?.propertyAddress ||
          applicationPayload?.btl?.solo?.propertyAddress ||
          applicationPayload?.btl?.limited?.propertyAddress ||
          applicationPayload?.btl?.partnership?.propertyAddress ||
          applicationPayload?.btl?.pension?.propertyAddress ||
          "",
        first_name: fullApp?.first_name || applicationPayload?.first_name || "",
        surname: fullApp?.surname || applicationPayload?.surname || "",
        email: fullApp?.email || applicationPayload?.email || "",
        phone_number: fullApp?.phone_number || applicationPayload?.phone_number || "",
        credit_history: fullApp?.credit_history || applicationPayload?.credit_history || "",
        number_of_properties_owned: fullApp?.number_of_properties_owned || applicationPayload?.number_of_properties_owned || 0,
        company_name: fullApp?.company_name || applicationPayload?.company_name || "",
        company_registration_number: fullApp?.company_registration_number || applicationPayload?.company_registration_number || "",
        company_telephone_number: fullApp?.company_telephone_number || applicationPayload?.company_telephone_number || "",
        company_email: fullApp?.company_email || applicationPayload?.company_email || "",
        trust_name: fullApp?.trust_name || applicationPayload?.trust_name || "",
        trust_number: fullApp?.trust_number || applicationPayload?.trust_number || "",
      };

      // Set editing application and open modal
      setEditingApplication({
        ...app,
        fullApp,
        applicationPayload: mergedPayload,
      });
      setEditModalOpen(true);
    } catch (error) {
      console.error("Failed to load application for editing:", error);
      showToast("error", "Failed to load application. Please try again.");
    }
  }

  function openDelete(app) {
    setDeleteConfirm("");
    setDeleteOpen(true);
  }

  async function confirmDelete() {
    const selected = apps.find((a) => a.id === selectedApplication);
    if (!selected) return;

    const expected = String(selected.id || "").trim();
    if (deleteConfirm.trim() !== expected) {
      showToast("error", "Please type the Application ID exactly to delete.");
      return;
    }

    setIsDeleting(true);
    try {
      await deleteApplication(selected.id);

      // Remove from local state
      setApps((prev) => prev.filter((a) => a.id !== selected.id));

      // Always navigate back to list view after deletion
      setSelectedApplication(null);

      setDeleteOpen(false);
      setDeleteConfirm("");
      showToast("success", "Application deleted successfully");
    } catch (error) {
      console.error("Failed to delete application:", error);
      showToast("error", "Failed to delete application. Please try again.");
    } finally {
      setIsDeleting(false);
    }
  }

  const downloadIllustration = (e) => {
    if (e) {
      e.preventDefault();
      e.stopPropagation();
    }

    // Prevent multiple simultaneous PDF generations
    if (isGeneratingPDF.current) {
      console.log("PDF generation already in progress");
      return;
    }

    isGeneratingPDF.current = true;

    // Capture current values to avoid closure issues
    const currentSelected = selectedApplication;
    const currentProductData = productData;
    const currentApps = apps;
    const currentProductsMap = productsMap;

    // Use setTimeout to completely isolate from React render cycle
    setTimeout(async () => {
      try {
        console.log("Download illustration clicked");

        if (!currentSelected) {
          console.error("No selected application");
          isGeneratingPDF.current = false;
          return;
        }

        const selected = currentApps.find((a) => a.id === currentSelected);
        if (!selected) {
          console.error("Application not found:", currentSelected);
          isGeneratingPDF.current = false;
          return;
        }

        // Get application payload
        let applicationPayload = selected.applicationPayload;
        if (!applicationPayload && selected._raw?.application_payload) {
          try {
            applicationPayload = typeof selected._raw.application_payload === "string"
              ? JSON.parse(selected._raw.application_payload)
              : selected._raw.application_payload;
          } catch (err) {
            console.warn("Failed to parse application_payload:", err);
            applicationPayload = {};
          }
        }
        applicationPayload = applicationPayload || {};

        const productId =
          selected.productId ||
          selected._raw?.product ||
          selected._raw?.product_id ||
          applicationPayload?.productId ||
          applicationPayload?.product_id ||
          "";

        // Resolve product: state → cache → API fetch → application-field fallback
        let resolvedProduct =
          currentProductData ||
          (productId ? currentProductsMap[productId] : null) ||
          null;

        if (!resolvedProduct && productId) {
          console.log("Product data missing in state; fetching:", productId);
          resolvedProduct = await getProductById(productId);
          if (resolvedProduct) {
            setProductData(resolvedProduct);
            setProductsMap((prev) => ({ ...prev, [productId]: resolvedProduct }));
          }
        }

        if (!resolvedProduct) {
          console.warn("Mortgage Product fetch unavailable; building from application fields");
          const raw = selected._raw || {};
          resolvedProduct = {
            name: productId || selected.id,
            product_id: productId || selected.id,
            id: productId || selected.id,
            product_name: selected.title || productId || selected.id,
            product_category: selected.productCategory || raw.product_type || "IF",
            category: selected.productCategory || raw.product_type || "IF",
            productType: selected.productCategory || raw.product_type || "IF",
            sector: raw.sector ?? applicationPayload.sector,
            facility_required: raw.facility_required ?? applicationPayload.facility_required ?? selected.facilityRequiredRaw,
            facility_type: raw.facility_type ?? applicationPayload.facility_type,
            net_advance: raw.net_advance ?? applicationPayload.net_advance,
            contract_term_months: raw.contract_term_months ?? applicationPayload.contract_term_months,
            termMonths: raw.contract_term_months ?? applicationPayload.contract_term_months,
            min_service_fee: raw.min_service_fee ?? applicationPayload.min_service_fee,
            ip_percent: raw.ip_percent ?? applicationPayload.ip_percent,
            service_fee_percent: raw.service_fee_percent ?? applicationPayload.service_fee_percent,
            annualCostPct: applicationPayload.service_fee_percent ?? raw.simple_service_fee,
            discount_rate_percent: raw.discount_rate_percent ?? applicationPayload.discount_rate_percent,
            apr: raw.apr ?? applicationPayload.apr,
            arrangement_fee: raw.arrangement_fee ?? applicationPayload.arrangement_fee,
            cost_over_product_term: raw.cost_over_product_term ?? applicationPayload.cost_over_product_term,
            additional_borrowing_amount: raw.additional_borrowing_amount ?? applicationPayload.additional_borrowing_amount,
            interest_rate: raw.interest_rate ?? applicationPayload.interest_rate,
            additional_borrowing_interest_rate: raw.interest_rate ?? raw.additional_borrowing_interest_rate ?? applicationPayload.additional_borrowing_interest_rate,
            first_12_month_interest: raw.first_12_month_interest ?? applicationPayload.first_12_month_interest,
            monthly_repayments: raw.monthly_repayments ?? applicationPayload.monthly_repayments,
            total_repayable: raw.total_repayable ?? applicationPayload.total_repayable,
            confidential_facility: raw.confidential_facility ?? applicationPayload.confidential_facility,
            disclosure: raw.disclosure ?? applicationPayload.disclosure,
            loan_amount: raw.loan_amount ?? selected.loanAmountRaw,
            mortgage_amount: raw.mortgage_amount ?? selected.loanAmountRaw,
            loan_purpose: raw.loan_purpose ?? applicationPayload.loan_purpose,
            ab_arrangement_fee: raw.ab_arrangement_fee ?? applicationPayload.ab_arrangement_fee,
            ab_cost_over_product_term:
              raw.ab_cost_over_product_term ??
              applicationPayload.ab_cost_over_product_term ??
              raw.ab_cost_over_term ??
              applicationPayload.ab_cost_over_term,
            abCalc: applicationPayload.abCalc || null,
          };
        }

        console.log("Selected application:", selected);
        console.log("Product data:", resolvedProduct);

        // Get loan amount from the application (from shortlisted products API)
        const applicationLoanAmount = selected.loanAmountRaw && selected.loanAmountRaw > 0
          ? selected.loanAmountRaw
          : null;

        // Get property value from the application (from shortlisted products API)
        const applicationPropertyValue = selected.propertyValueRaw && selected.propertyValueRaw > 0
          ? selected.propertyValueRaw
          : null;

        console.log("Application loan amount:", applicationLoanAmount);
        console.log("Application property value:", applicationPropertyValue);

        // Use backend response repayment_type (e.g. "Interest only") for illustration, not submission payload (repayment_method: "Repayment")
        // Cost over product term: API often returns it at document level (selected._raw), or inside application_payload
        const costOverProductTerm =
          selected._raw?.cost_over_product_term ??
          applicationPayload?.cost_over_product_term ??
          resolvedProduct?.cost_over_product_term ??
          resolvedProduct?.total_cost ??
          resolvedProduct?.totalCost ??
          resolvedProduct?._raw?.cost_over_product_term ??
          resolvedProduct?._raw?.total_cost;

        // Ensure illustration gets application's loan_amount, cost_over_product_term, and monthly_payment (API may return at doc level)
        const applicationLoanAmountForPayload = selected._raw?.loan_amount ?? selected._raw?.mortgage_amount ?? selected.loanAmountRaw ?? applicationPayload?.loan_amount ?? applicationPayload?.mortgageAmount;
        const monthlyPaymentForIllustration =
          applicationPayload?.monthly_payment ??
          selected._raw?.monthly_payment ??
          resolvedProduct?.monthly_payment ??
          resolvedProduct?.monthlyPayment ??
          resolvedProduct?.monthly_repayments ??
          resolvedProduct?.repayment;

        const payloadForIllustration = {
          ...(applicationPayload || {}),
          repayment_type: selected._raw?.repayment_type ?? selected._raw?.repaymentType ?? applicationPayload?.repayment_type ?? applicationPayload?.repaymentType ?? "Repayment",
          repaymentType: selected._raw?.repayment_type ?? selected._raw?.repaymentType ?? applicationPayload?.repaymentType ?? applicationPayload?.repayment_type ?? "Repayment",
          loan_amount: applicationLoanAmountForPayload ?? applicationPayload?.loan_amount,
          mortgageAmount: applicationLoanAmountForPayload ?? applicationPayload?.mortgageAmount,
          cost_over_product_term: costOverProductTerm,
          monthly_payment: monthlyPaymentForIllustration ?? applicationPayload?.monthly_payment,
          productType: selected.productCategory || selected.productType || applicationPayload?.productType || selected._raw?.product_type,
          product_type: selected._raw?.product_type || applicationPayload?.product_type,
          loan_purpose: selected._raw?.loan_purpose || applicationPayload?.loan_purpose,
          sector: selected._raw?.sector ?? applicationPayload?.sector ?? resolvedProduct?.sector,
          facility_required: selected._raw?.facility_required ?? applicationPayload?.facility_required ?? selected.facilityRequiredRaw,
          facility_type: selected._raw?.facility_type ?? applicationPayload?.facility_type ?? resolvedProduct?.facility_type,
          net_advance: selected._raw?.net_advance ?? applicationPayload?.net_advance ?? resolvedProduct?.net_advance,
          contract_term_months: selected._raw?.contract_term_months ?? applicationPayload?.contract_term_months ?? resolvedProduct?.contract_term_months ?? resolvedProduct?.termMonths,
          min_service_fee: selected._raw?.min_service_fee ?? applicationPayload?.min_service_fee ?? resolvedProduct?.min_service_fee,
          ip_percent: selected._raw?.ip_percent ?? applicationPayload?.ip_percent ?? resolvedProduct?.ip_percent,
          simple_service_fee:
            selected._raw?.simple_service_fee ??
            applicationPayload?.simple_service_fee ??
            resolvedProduct?.simple_service_fee,
          service_fee_percent:
            selected._raw?.service_fee_percent ??
            applicationPayload?.service_fee_percent ??
            resolvedProduct?.service_fee_percent ??
            resolvedProduct?.annualCostPct ??
            selected._raw?.simple_service_fee ??
            applicationPayload?.simple_service_fee ??
            resolvedProduct?.simple_service_fee,
          discount_rate_percent: selected._raw?.discount_rate_percent ?? applicationPayload?.discount_rate_percent ?? resolvedProduct?.discount_rate_percent,
          apr: selected._raw?.apr ?? applicationPayload?.apr ?? resolvedProduct?.apr,
          arrangement_fee: selected._raw?.arrangement_fee ?? applicationPayload?.arrangement_fee ?? resolvedProduct?.arrangement_fee,
          additional_borrowing_amount: selected._raw?.additional_borrowing_amount ?? applicationPayload?.additional_borrowing_amount ?? resolvedProduct?.additional_borrowing_amount,
          interest_rate: selected._raw?.interest_rate ?? applicationPayload?.interest_rate ?? resolvedProduct?.interest_rate,
          additional_borrowing_interest_rate: selected._raw?.interest_rate ?? selected._raw?.additional_borrowing_interest_rate ?? applicationPayload?.additional_borrowing_interest_rate ?? resolvedProduct?.additional_borrowing_interest_rate,
          first_12_month_interest: selected._raw?.first_12_month_interest ?? applicationPayload?.first_12_month_interest ?? resolvedProduct?.first_12_month_interest,
          monthly_repayments: selected._raw?.monthly_repayments ?? applicationPayload?.monthly_repayments ?? resolvedProduct?.monthly_repayments,
          total_repayable: selected._raw?.total_repayable ?? applicationPayload?.total_repayable ?? resolvedProduct?.total_repayable,
          confidential_facility: selected._raw?.confidential_facility ?? applicationPayload?.confidential_facility ?? resolvedProduct?.confidential_facility,
          disclosure: selected._raw?.disclosure ?? applicationPayload?.disclosure ?? resolvedProduct?.disclosure,
          ab_arrangement_fee:
            selected._raw?.ab_arrangement_fee ??
            applicationPayload?.ab_arrangement_fee ??
            resolvedProduct?.ab_arrangement_fee,
          ab_cost_over_product_term:
            selected._raw?.ab_cost_over_product_term ??
            applicationPayload?.ab_cost_over_product_term ??
            selected._raw?.ab_cost_over_term ??
            applicationPayload?.ab_cost_over_term ??
            resolvedProduct?.ab_cost_over_product_term ??
            resolvedProduct?.ab_cost_over_term,
          abCalc: resolvedProduct?.abCalc || applicationPayload?.abCalc || null,
        };

        // Extract illustration data - pass loan amount and property value from application
        const illustrationData = extractIllustrationData(
          {
            ...resolvedProduct,
            name: resolvedProduct?.name || productId || resolvedProduct?.product_id,
            product_id: resolvedProduct?.product_id || resolvedProduct?.name || productId,
            productId: resolvedProduct?.productId || resolvedProduct?.name || productId,
            mortgage_product: resolvedProduct?.mortgage_product || productId,
            product_category: selected.productCategory || resolvedProduct?.product_category || resolvedProduct?.category,
            category: selected.productCategory || resolvedProduct?.category,
            productType: selected.productCategory || resolvedProduct?.productType,
            simple_service_fee:
              resolvedProduct?.simple_service_fee ??
              selected._raw?.simple_service_fee ??
              applicationPayload?.simple_service_fee,
            annualCostPct:
              resolvedProduct?.annualCostPct ??
              resolvedProduct?.service_fee_percent ??
              selected._raw?.service_fee_percent ??
              selected._raw?.simple_service_fee ??
              applicationPayload?.simple_service_fee,
          },
          payloadForIllustration,
          applicationLoanAmount ?? selected.facilityRequiredRaw,
          applicationPropertyValue
        );
        console.log("Illustration data:", illustrationData);

        const isIfIllustration =
          illustrationData?.isInvoiceFinance || isInvoiceFinanceApplication(selected);

        // Get client details from application or borrower profile (including property address from submitted application)
        const propertyAddress = isIfIllustration
          ? ""
          : (
            selected?.propertyAddress ||
            selected._raw?.property_address ||
            selected._raw?.propertyAddress ||
            applicationPayload?.property_address ||
            applicationPayload?.propertyAddress ||
            applicationPayload?.formData?.propertyAddress ||
            applicationPayload?.formData?.property_address ||
            applicationPayload?.btl?.solo?.propertyAddress ||
            applicationPayload?.btl?.solo?.property_address ||
            applicationPayload?.btl?.limited?.propertyAddress ||
            applicationPayload?.btl?.partnership?.propertyAddress ||
            applicationPayload?.btl?.pension?.propertyAddress ||
            ""
          );
        const ownershipType = selected._raw?.ownership_type ?? applicationPayload?.ownership_type ?? "";
        const companyName = selected._raw?.company_name ??
          applicationPayload?.company_name ??
          applicationPayload?.btl?.limited?.company?.name ??
          "";
        const clientDetails = {
          name: selected._raw?.first_name && selected._raw?.surname
            ? `${selected._raw.first_name} ${selected._raw.surname}`
            : applicationPayload?.formData?.firstName && applicationPayload?.formData?.surname
              ? `${applicationPayload.formData.firstName} ${applicationPayload.formData.surname}`
              : getBorrowerEmail()?.split('@')[0] || "Client",
          propertyAddress: typeof propertyAddress === "string" ? propertyAddress : (propertyAddress?.address || ""),
          telephone: selected._raw?.phone_number ||
            applicationPayload?.formData?.phone ||
            applicationPayload?.formData?.applicants?.[0]?.phone || "",
          email: selected._raw?.email ||
            applicationPayload?.formData?.email ||
            applicationPayload?.formData?.applicants?.[0]?.email ||
            getBorrowerEmail() || "",
          ownershipType: ownershipType || undefined,
          companyName: companyName || undefined,
        };

        console.log("Client details:", clientDetails);

        // Generate and download PDF (loads polar bear logo from public folder)
        console.log("Generating PDF...");
        try {
          await generateClientIllustrationPDFWithLogo(illustrationData, clientDetails);
          console.log("PDF generation completed");
        } catch (pdfError) {
          console.error("PDF generation error:", pdfError);
        } finally {
          isGeneratingPDF.current = false;
        }
      } catch (error) {
        console.error("Failed to generate illustration:", error);
        console.error("Error stack:", error.stack);
        isGeneratingPDF.current = false;
        // Don't call showToast here to avoid React state updates
      }
    }, 100);
  };

  // ===== Detail View =====
  if (selectedApplication) {
    const selected = apps.find((a) => a.id === selectedApplication);

    if (!selected) {
      return (
        <div className="min-h-dvh bg-white">
          <Hero
            className="-mx-6"
            title="Applications"
            subtitle="Track and manage your submissions."
            right={
              <button
                onClick={() => setSelectedApplication(null)}
                className="inline-flex items-center gap-2 rounded-xl bg-white text-sky-700 px-3 py-2 font-semibold hover:bg-slate-100"
              >
                <ArrowLeft className="h-4 w-4" />
                Back to list
              </button>
            }
          />
          <div className="mx-auto max-w-7xl p-4 sm:p-6">
            <div className="rounded-2xl border border-slate-200 bg-white p-6 text-slate-600">
              Could not find that application.
            </div>
          </div>
        </div>
      );
    }

    const progressPct = selected?.progressPct ?? 0;

    // Get loan amount from multiple sources in priority order:
    // 1. Product data (if fetched)
    // 2. Application's loan_amount
    // 3. Application payload (shortlisted product data)
    let loanAmountValue = null;

    // First, try product data
    if (productData) {
      loanAmountValue = productData.loan_amount_full_details ||
        productData.loan_amount_full ||
        productData.mortgage_amount ||
        productData.loan_amount ||
        productData.amount ||
        productData.principal_amount ||
        productData.borrowing_amount ||
        productData.loanAmount ||
        productData.mortgageAmount ||
        null;
    }

    // If not from product, try application's loan_amount
    if (!loanAmountValue && selected.loanAmountRaw && selected.loanAmountRaw > 0) {
      loanAmountValue = selected.loanAmountRaw;
    }

    // If still not found, try application_payload (check all possible locations)
    if (!loanAmountValue && selected.applicationPayload) {
      const payload = selected.applicationPayload;
      loanAmountValue = payload?.loan_amount ||
        payload?.mortgageAmount ||
        payload?.mortgage_amount ||
        payload?.loanAmount ||
        payload?.principal_amount ||
        payload?.borrowing_amount ||
        payload?.formData?.loan_amount ||
        payload?.formData?.mortgageAmount ||
        payload?.formData?.loanAmount ||
        payload?.fullPayload?.loan_amount ||
        payload?.fullPayload?.mortgageAmount ||
        payload?.btl?.solo?.loan_amount ||
        payload?.btl?.limited?.loan_amount ||
        payload?.btl?.partnership?.loan_amount ||
        payload?.btl?.pension?.loan_amount ||
        null;
    }

    const displayLoanAmount = loanAmountValue ? money(loanAmountValue) : (selected.loanAmount && selected.loanAmount !== "—" ? selected.loanAmount : "—");

    const isIfSelected = isInvoiceFinanceApplication(selected);
    let facilityRequiredValue = null;
    if (isIfSelected) {
      facilityRequiredValue =
        selected.facilityRequiredRaw ??
        selected._raw?.facility_required ??
        selected.applicationPayload?.facility_required ??
        selected.applicationPayload?.facilityRequired ??
        productData?.facility_required ??
        productData?.facilityRequired ??
        loanAmountValue ??
        selected.loanAmountRaw ??
        null;
    }
    const displayFacilityRequired = facilityRequiredValue
      ? money(facilityRequiredValue)
      : displayLoanAmount;
    return (
      <div className="ta-page">
        <style>{css}</style>

        <div className="ta-shell">
          {/* Header Row: Selector + Actions */}
          <div className="ta-topbar">
            <div className="ta-selectWrap">
              <div className="ta-selectRow">
                <button
                  onClick={() => setSelectedApplication(null)}
                  className="inline-flex items-center gap-2 rounded-xl bg-white text-sky-700 px-3 py-2 font-semibold hover:bg-slate-100 border border-slate-200 mr-2"
                  title="Back to list"
                >
                  <ArrowLeft className="h-4 w-4" />
                </button>

                <div className="ta-actions">
                  <button
                    className="ta-btn ta-btnUpdate"
                    onClick={() => openEdit(selected)}
                    disabled={!selected}
                    title="Update application"
                  >
                    <Pencil className="h-4 w-4" />
                    Update
                  </button>
                  <button
                    className="ta-btn ta-btnDelete"
                    onClick={() => openDelete(selected)}
                    disabled={!selected}
                    title="Delete application"
                  >
                    <Trash2 className="h-4 w-4" />
                    Delete
                  </button>
                </div>
              </div>
            </div>

            <div className="ta-mutedRight">
              <button
                onClick={handleRefresh}
                disabled={isRefreshing}
                className="ta-btn ta-btnSecondary mr-3"
                title="Refresh applications"
              >
                <RefreshCw className={`h-4 w-4 ${isRefreshing ? 'animate-spin' : ''}`} />
                {isRefreshing ? 'Refreshing...' : 'Refresh'}
              </button>
              <span className="ta-clock">
                <Calendar className="h-4 w-4" />
                Last updated: {selected._raw?.modified ? formatDateTime(selected._raw.modified) : formatDateTime(selected.submitted_on) || "—"}
              </span>
            </div>
          </div>

          {/* Top Card (like screenshot) */}
          <section className="ta-card">
            <div className="ta-cardHeader">
              <div className="min-w-0">
                <div className="flex flex-wrap items-start gap-2">
                  <ApplicationTypeWithChips
                    as="h1"
                    label={selected.title}
                    product={applicationPropertyChipProduct(selected, { productData })}
                  />
                  {(() => {
                    const pill = getApplicationStatusPill(selected.status);
                    return (
                      <span className={pill.className} role="status">
                        {pill.label}
                      </span>
                    );
                  })()}
                </div>
                <div className="ta-appIdValue ta-appIdValue--mono">{selected.id}</div>
              </div>
            </div>

            <div className="ta-kpiProgressSection">
              <div className="ta-kpis">
                <KpiCard
                  tone="blue"
                  label={isIfSelected ? "Facility Required" : "Loan Amount"}
                  value={
                    isIfSelected
                      ? displayFacilityRequired
                      : loadingProduct && !displayLoanAmount
                        ? "Loading..."
                        : displayLoanAmount
                  }
                />
                <KpiCard tone="teal" label="Submitted" value={formatDate(selected.submitted)} />
                <KpiCard
                  tone="indigo"
                  label="Est. Completion"
                  value={formatDate(selected.estCompletion)}
                />
                <KpiCard tone="magenta" label="Current Stage" value={selected.currentStage} />
              </div>

              <div className="ta-progressBlock">
                <div className="ta-progressLabelRow">
                  <div className="ta-progressLabel">Application Progress</div>
                  <div className="ta-progressPct">{progressPct}%</div>
                </div>

                <div className="ta-progressTrack" role="progressbar" aria-valuenow={progressPct} aria-valuemin={0} aria-valuemax={100}>
                  <div className="ta-progressFill" style={{ width: `${progressPct}%` }} />
                </div>
              </div>
            </div>

            <div className="ta-illustration">
              <div className="ta-illusLeft">
                <div className="ta-illusIcon">
                  <FileText className="h-5 w-5" />
                </div>
                <div>
                  <div className="ta-illusTitle">
                    {isIfSelected ? "Invoice Finance Illustration" : "Mortgage Illustration"}
                  </div>
                  <div className="ta-illusSub">Download your personalised illustration document</div>
                </div>
              </div>

              <button className="ta-btn ta-btnPrimary" onClick={downloadIllustration}>
                <Download className="h-4 w-4" />
                Download
              </button>
            </div>

            <div className="grid grid-cols-1 gap-3 md:grid-cols-2">
              <div className="ta-manager ta-manager--case">
                {(() => {
                  const cm = selected.caseManager || {};
                  const hasAssigned =
                    cm.name && cm.name !== "Case Manager" && cm.name !== "—" && cm.email;
                  if (!hasAssigned) {
                    return (
                      <>
                        <div className="ta-managerLeft">
                          <div className="ta-avatar">CM</div>
                          <div>
                            <div className="ta-managerLabel">Your dedicated case manager</div>
                            <div className="ta-managerName">We’ll assign a case manager shortly</div>
                          </div>
                        </div>
                      </>
                    );
                  }

                  const name = cm.name;
                  const email = cm.email;
                  const initials =
                    cm.initials ||
                    name
                      .split(" ")
                      .map((n) => n[0])
                      .join("")
                      .toUpperCase()
                      .slice(0, 2) ||
                    "CM";

                  return (
                    <>
                      <div className="ta-managerLeft">
                        <div className="ta-avatar">{initials}</div>
                        <div>
                          <div className="ta-managerLabel">Your dedicated case manager</div>
                          <div className="ta-managerName">{name}</div>
                        </div>
                      </div>

                      <div className="ta-managerRight">
                        <a className="ta-link" href={`mailto:${email}`}>
                          <Mail className="h-4 w-4" />
                          {email}
                        </a>
                      </div>
                    </>
                  );
                })()}
              </div>

              <div className="ta-manager ta-manager--enquiries">
                <div className="ta-managerLeft">
                  <div className="ta-avatar">JM</div>
                  <div>
                    <div className="ta-managerLabel">For general enquiries</div>
                    <div className="ta-managerName">Josh Mander</div>
                  </div>
                </div>
                <div className="ta-managerRight">
                  <a className="ta-link" href="mailto:admin@dynamicCF.co.uk">
                    <Mail className="h-4 w-4" />
                    admin@dynamicCF.co.uk
                  </a>
                </div>
              </div>
            </div>
          </section>

          {/* Activity Card */}
          <section className="ta-card ta-cardActivity">
            <div className="ta-activityHeader">
              <div className="ta-activityTitle">Activity</div>
            </div>

            <div className="ta-timelineWrap">
              <div className="ta-timelineLine" aria-hidden="true" />

              <ol className="ta-steps" aria-label="Application stages">
                {selected.activity.map((step, idx) => {
                  const isDone = step.status === "completed";
                  const isCurrent = step.status === "inprogress";
                  const pillLabel = isDone ? "Complete" : isCurrent ? "In progress" : "Pending";
                  const stageColors = getStageTimelineColors(step.key === "valuation" && /audit/i.test(step.title) ? "audit" : step.key || step.title);
                  const pillClass = isDone
                    ? "ta-pill ta-pill-completed"
                    : isCurrent
                      ? "ta-pill ta-pill-inprogress"
                      : "ta-pill ta-pill-pending";
                  const circleStyle =
                    isDone || isCurrent
                      ? {
                          background: stageColors.solid,
                          borderColor: stageColors.solid,
                          color: "#fff",
                        }
                      : {
                          background: "#fff",
                          borderColor: stageColors.soft,
                          color: "#94a3b8",
                        };
                  const titleStyle =
                    isDone || isCurrent
                      ? { color: stageColors.solid }
                      : undefined;

                  return (
                    <li
                      key={`${step.key}-${idx}`}
                      className="ta-step-new"
                      aria-current={isCurrent ? "step" : undefined}
                    >
                      <div className="ta-stepNodeWrap">
                        <div className="ta-stepCircleWrap">
                          <div
                            className={
                              "ta-stepCircle-v2 " +
                              (isDone ? "is-done" : isCurrent ? "is-current" : "is-pending")
                            }
                            style={circleStyle}
                            aria-hidden="true"
                          >
                            {isDone ? (
                              <Check className="h-4 w-4" />
                            ) : isCurrent ? (
                              <Clock className="h-4 w-4" />
                            ) : null}
                          </div>

                          {STAGE_INFO_TEXT[step.key] && (
                            <button
                              type="button"
                              id={`info-btn-${step.key}-${idx}`}
                              className="ta-stageInfoBtnCircle"
                              onClick={(e) => {
                                e.stopPropagation();
                                setActiveInfoPopup(activeInfoPopup === step.key ? null : step.key);
                              }}
                              title="Click for information"
                              aria-label={`Information about ${step.title}`}
                              aria-expanded={activeInfoPopup === step.key}
                            >
                              <Info className="ta-stageInfoIcon" aria-hidden="true" />
                            </button>
                          )}
                        </div>
                      </div>
                      <div className="ta-stepContent-new">
                        <div className="ta-stepTitle-new" style={titleStyle}>{step.title}</div>
                        {(isDone || isCurrent) && step.date ? (
                          <div className="ta-stepMeta" title="When this activity changed">
                            <Calendar className="ta-stepMetaIcon" aria-hidden="true" />
                            <span>{formatDateTime(step.date)}</span>
                          </div>
                        ) : null}
                        <span
                          className={pillClass}
                          style={
                            isCurrent
                              ? {
                                  background: `${stageColors.soft}`,
                                  color: stageColors.solid,
                                  borderColor: stageColors.soft,
                                }
                              : isDone
                                ? {
                                    background: `${stageColors.soft}`,
                                    color: stageColors.solid,
                                    borderColor: stageColors.soft,
                                  }
                                : undefined
                          }
                        >
                          {pillLabel}
                        </span>
                      </div>
                    </li>
                  );
                })}
              </ol>
            </div>
          </section>

          {activeInfoPopup && (
            <div
              className="ta-stageInfoBackdrop"
              onClick={() => setActiveInfoPopup(null)}
              aria-hidden="true"
            />
          )}

          {activeInfoPopup && (() => {
            const activeIdx = selected.activity.findIndex((s) => s.key === activeInfoPopup);
            const activeStep = activeIdx >= 0 ? selected.activity[activeIdx] : null;
            if (!activeStep || !STAGE_INFO_TEXT[activeStep.key]) return null;
            const infoKey =
              activeStep.key === "valuation" && isInvoiceFinanceApplication(selected)
                ? "audit"
                : activeStep.key;

            return (
              <StageInfoPopup
                anchorId={`info-btn-${activeStep.key}-${activeIdx}`}
                title={activeStep.title}
                text={STAGE_INFO_TEXT[infoKey] || STAGE_INFO_TEXT[activeStep.key]}
                onClose={() => setActiveInfoPopup(null)}
              />
            );
          })()}

        </div>

        {/* Toast */}
        {toast && (
          <div className={`ta-toast ta-toast-${toast.type}`}>
            {toast.type === "success" ? <CheckCircle className="h-4 w-4" /> : <AlertCircle className="h-4 w-4" />}
            <span>{toast.message}</span>
          </div>
        )}

        {completionCongratsOpen && (
          <CompletionCongratsModal
            applicationId={selected.id}
            productType={selected.productType}
            onClose={() => setCompletionCongratsOpen(false)}
          />
        )}

        <ReviewPromptModal
          open={postSubmitReviewOpen}
          onClose={() => setPostSubmitReviewOpen(false)}
          productCategory={selected?.productType || null}
          product={selected?.productId || selected?.product || null}
          contextLabel="Thanks for submitting — how was the application process?"
          defaultType="General Feedback"
        />

        {/* Delete Modal */}
        {deleteOpen && selected && (
          <Modal
            title="Delete application"
            onClose={() => setDeleteOpen(false)}
            footer={
              <>
                <button className="ta-btn ta-btnSecondary" onClick={() => setDeleteOpen(false)}>
                  Cancel
                </button>
                <button
                  className="ta-btn ta-btnDelete"
                  onClick={confirmDelete}
                  disabled={isDeleting}
                >
                  {isDeleting ? "Deleting..." : "Delete"}
                </button>
              </>
            }
          >
            <div className="ta-dangerBox">
              <div className="ta-dangerTitle">This action cannot be undone.</div>
              <div className="ta-dangerText">
                To confirm, type the Application ID below:
                <div className="ta-dangerId">{selected.id}</div>
              </div>

              <input
                className="ta-input"
                value={deleteConfirm}
                onChange={(e) => setDeleteConfirm(e.target.value)}
                placeholder="Type Application ID"
              />

              <div className="ta-dangerNote">
                If you wire this to your backend, ensure you also delete/void any related documents
                (uploads, notes, tracking stages).
              </div>
            </div>
          </Modal>
        )}

        {/* Edit Application Modal */}
        {editModalOpen && editingApplication && (
          <EditApplicationModal
            application={editingApplication}
            onClose={() => {
              setEditModalOpen(false);
              setEditingApplication(null);
            }}
            onSave={async () => {
              // Reload applications list to reflect changes
              await load();
              setEditModalOpen(false);
              setEditingApplication(null);
            }}
          />
        )}
      </div>
    );
  }

  // ===== List View =====
  return (
    <div className="min-h-dvh bg-slate-50">
      <Hero
        className="-mx-6"
        title="Applications"
        subtitle="Track and manage your submissions."
        right={
          <button
            onClick={handleRefresh}
            disabled={isRefreshing}
            className="inline-flex items-center gap-2 rounded-xl bg-white text-sky-700 px-4 py-2 font-semibold hover:bg-slate-100 border border-slate-200"
            title="Refresh applications"
          >
            <RefreshCw className={`h-4 w-4 ${isRefreshing ? 'animate-spin' : ''}`} />
            {isRefreshing ? 'Refreshing...' : 'Refresh'}
          </button>
        }
      />

      <div className="mx-auto max-w-7xl p-4 sm:p-6">
        {/* Error Message */}
        {err && (
          <div className="mb-6 rounded-xl border border-rose-200 bg-rose-50 p-4">
            <p className="text-sm font-medium text-rose-800">{err}</p>
            <button
              onClick={load}
              className="mt-2 text-sm font-semibold text-rose-700 hover:text-rose-900 underline"
            >
              Try again
            </button>
          </div>
        )}

        {/* Loading State */}
        {loading && (
          <div className="flex items-center justify-center py-12">
            <Loader2 className="h-8 w-8 animate-spin text-sky-600" />
            <span className="ml-3 text-slate-600">Loading applications...</span>
          </div>
        )}

        {/* Empty State */}
        {!loading && !err && apps.length === 0 && (
          <div className="rounded-2xl border border-slate-200 bg-white p-12 text-center shadow-sm">
            <div className="w-16 h-16 rounded-full bg-slate-100 mx-auto flex items-center justify-center mb-4">
              <FileText className="h-8 w-8 text-slate-400" />
            </div>
            <h3 className="text-lg font-semibold text-slate-900">No applications found</h3>
            <p className="mt-2 text-sm text-slate-600">
              You haven't submitted any applications yet.
            </p>
          </div>
        )}

        {/* Applications List */}
        {!loading && apps.length > 0 && (
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
            {apps.map((app, index) => {
              const statusColors = {
                "enquiry": "bg-blue-100 text-blue-700 border-blue-200",
                "application": "bg-sky-100 text-sky-700 border-sky-200",
                "approval": "bg-purple-100 text-purple-700 border-purple-200",
                "valuation": "bg-amber-100 text-amber-700 border-amber-200",
                "audit": "bg-amber-100 text-amber-700 border-amber-200",
                "legals": "bg-pink-100 text-pink-700 border-pink-200",
                "completion": "bg-emerald-100 text-emerald-700 border-emerald-200",
              };

              const statusKey = (app.status || "").toLowerCase();
              const statusColor = Object.keys(statusColors).find(k => statusKey.includes(k))
                ? statusColors[Object.keys(statusColors).find(k => statusKey.includes(k))]
                : "bg-slate-100 text-slate-700 border-slate-200";

              const borderColor = "border-[#e7eaef] hover:border-sky-300";

              const cardGradients = [
                "from-blue-500 to-cyan-600",
                "from-purple-500 to-pink-600",
                "from-emerald-500 to-teal-600",
                "from-amber-500 to-orange-600",
                "from-indigo-500 to-blue-600",
                "from-rose-500 to-pink-600",
                "from-violet-500 to-purple-600",
                "from-green-500 to-emerald-600",
              ];
              const borderColors = [
                "border-blue-300 hover:border-blue-400",
                "border-purple-300 hover:border-purple-400",
                "border-emerald-300 hover:border-emerald-400",
                "border-amber-300 hover:border-amber-400",
                "border-indigo-300 hover:border-indigo-400",
                "border-rose-300 hover:border-rose-400",
                "border-violet-300 hover:border-violet-400",
                "border-green-300 hover:border-green-400",
              ];
              const gradientIndex = index % cardGradients.length;
              const cardGradient = cardGradients[gradientIndex];
              const listBorderColor = borderColors[gradientIndex];

              return (
                <div
                  key={app.id}
                  onClick={() => setSelectedApplication(app.id)}
                  className={`group cursor-pointer bg-white rounded-2xl border-2 ${listBorderColor} p-4 md:p-6 shadow-sm hover:shadow-lg transition-all duration-300 hover:-translate-y-0.5`}
                >
                  <div className="flex items-start justify-between mb-4">
                    <div className="flex-1">
                      <div className="flex items-center gap-2 mb-2">
                        <div className={`w-10 h-10 rounded-xl bg-gradient-to-br ${cardGradient} flex items-center justify-center text-white font-bold text-sm shadow-md`}>
                          {app.productType.charAt(0)}
                        </div>
                        <div className="min-w-0">
                          <ApplicationTypeWithChips
                            label={app.productType}
                            product={applicationPropertyChipProduct(app)}
                            compact
                          />
                          <p className="text-xs text-slate-500">#{app.id}</p>
                        </div>
                      </div>
                    </div>
                    <div className="flex flex-col items-end gap-2">
                      <span className={`px-3 py-1 rounded-full text-xs font-semibold border ${statusColor}`}>
                        {app.status}
                      </span>
                      {/* Property Address directly under stage badge */}
                      {app.propertyAddress && (
                        <p className="text-sm font-extrabold text-black line-clamp-2 text-right max-w-[200px]">
                          {app.propertyAddress}
                        </p>
                      )}
                    </div>
                  </div>

                  {/* Key Info */}
                  <div className="space-y-3 mb-4">
                    <div className="flex items-center justify-between">
                      <span className="text-sm text-slate-600">
                        {app.productCategory === "IF" || isInvoiceFinanceApplication(app)
                          ? "Facility Required"
                          : "Loan Amount"}
                      </span>
                      <span className="font-bold text-slate-900">
                        {app.productCategory === "IF" || isInvoiceFinanceApplication(app)
                          ? (app.facilityRequiredRaw
                            ? money(app.facilityRequiredRaw)
                            : app.loanAmount)
                          : app.loanAmount}
                      </span>
                    </div>
                    <div className="flex items-center justify-between">
                      <span className="text-sm text-slate-600">Submitted</span>
                      <span className="font-semibold text-slate-900">{formatDate(app.submittedDate)}</span>
                    </div>
                  </div>

                  {/* Progress */}
                  <div className="pt-4 border-t border-slate-100">
                    <div className="flex items-center justify-between text-xs text-slate-600 mb-2">
                      <span>Progress</span>
                      <span className="font-bold">{app.progress}%</span>
                    </div>
                    <div className="h-2 w-full overflow-hidden rounded-full bg-slate-200">
                      <div
                        className="h-full bg-gradient-to-r from-sky-500 via-blue-500 to-purple-500 transition-all duration-500"
                        style={{ width: `${app.progress}%` }}
                      />
                    </div>
                  </div>

                  {/* View Details CTA */}
                  <div className="mt-4 pt-4 border-t border-slate-100">
                    <div className="flex items-center justify-between text-sm">
                      <span className="text-slate-600">View details</span>
                      <ChevronRight className="h-4 w-4 text-slate-400 group-hover:text-sky-600 transition" />
                    </div>
                  </div>
                </div>
              );
            })}
          </div>
        )}
      </div>

      <ReviewPromptModal
        open={postSubmitReviewOpen}
        onClose={() => setPostSubmitReviewOpen(false)}
        contextLabel="Thanks for submitting — how was the application process?"
        defaultType="General Feedback"
      />
    </div>
  );
}

/* -----------------------------
   Components
----------------------------- */

function getApplicationStatusPill(status) {
  const s = String(status || "").toLowerCase();
  if (["approved", "accepted", "completed", "completion"].some((k) => s.includes(k))) {
    return { label: "Approved", className: "ta-statusPill ta-statusPill--approved" };
  }
  if (["pending", "in progress", "in_progress", "submitted", "under_review", "enquiry", "application", "valuation", "audit", "legal", "approval"].some((k) => s.includes(k))) {
    return { label: "In progress", className: "ta-statusPill ta-statusPill--inprogress" };
  }
  return { label: status || "In progress", className: "ta-statusPill ta-statusPill--inprogress" };
}

function CompletionCongratsModal({ applicationId, productType, onClose }) {
  return (
    <div className="ta-completionOverlay" role="dialog" aria-modal="true" aria-labelledby="ta-completion-title">
      <button type="button" className="ta-completionBackdrop" onClick={onClose} aria-label="Close congratulations" />
      <div className="ta-completionCard">
        <button type="button" className="ta-completionClose" onClick={onClose} aria-label="Close">
          <X className="h-4 w-4" />
        </button>
        <div className="ta-completionIconWrap" aria-hidden="true">
          <PartyPopper className="h-8 w-8" />
        </div>
        <h2 id="ta-completion-title" className="ta-completionTitle">Congratulations!</h2>
        <p className="ta-completionText">
          Your {productType ? `${productType} ` : ""}mortgage application has reached completion.
          Funds have been released and your application is now complete.
        </p>
        {applicationId ? (
          <p className="ta-completionRef">Application ref: {applicationId}</p>
        ) : null}
        <button type="button" className="ta-completionBtn" onClick={onClose}>
          Wonderful, thanks!
        </button>
      </div>
    </div>
  );
}

function KpiCard({ tone, label, value }) {
  return (
    <div className={`ta-kpi ta-kpi-${tone}`}>
      <div className="ta-kpiLabel">{label}</div>
      <div className="ta-kpiValue">{value}</div>
    </div>
  );
}



function Modal({ title, children, onClose, footer }) {
  return (
    <div className="ta-modalOverlay" role="dialog" aria-modal="true" onClick={(e) => e.target === e.currentTarget && onClose()}>
      <div className="ta-modal">
        <div className="ta-modalHeader">
          <div className="ta-modalTitle">{title}</div>
          <button className="ta-iconBtn" onClick={onClose} aria-label="Close">
            <X className="h-4 w-4" />
          </button>
        </div>
        <div className="ta-modalBody">{children}</div>
        <div className="ta-modalFooter">{footer}</div>
      </div>
    </div>
  );
}

/* -----------------------------
   Edit Application Modal
----------------------------- */
function Field({ label, children }) {
  return (
    <div className="ta-field">
      <label className="ta-fieldLabel">{label}</label>
      {children}
    </div>
  );
}

function EditApplicationModal({ application, onClose, onSave }) {
  const [isSaving, setIsSaving] = useState(false);
  const [isLoadingFiles, setIsLoadingFiles] = useState(true);
  const [uploadingFiles, setUploadingFiles] = useState({}); // Track uploading files with progress: { bucketKey: [{ id, name, progress }] }
  const [showSuccessDialog, setShowSuccessDialog] = useState(false);
  const applicationName = application?.id;
  const borrowerType = (application?.borrowerType || application?.applicationPayload?.btl?.borrowerType || application?.applicationPayload?.if?.borrowerType || "solo").toLowerCase();
  const fullApp = application?.fullApp || {};
  const isIfApp = isInvoiceFinanceApplication({
    ...application,
    fullApp,
    id: application?.id || fullApp?.name,
    productType: application?.productType || application?.productCategory || fullApp?.product_type,
    productCategory: application?.productCategory || fullApp?.product_type,
  });

  // Start with applicationPayload if it exists (has the correct nested structure)
  let applicationPayload = application?.applicationPayload || {};

  // If applicationPayload is empty or incomplete, build it from fullApp (flattened fields)
  const needsMapping = !applicationPayload.formData || !applicationPayload.btl || Object.keys(applicationPayload.btl).length === 0;

  if (needsMapping && fullApp && Object.keys(fullApp).length > 0) {
    if (borrowerType === "individual" || borrowerType === "solo") {
      applicationPayload = {
        formData: {
          firstName: applicationPayload.formData?.firstName || fullApp.first_name || "",
          surname: applicationPayload.formData?.surname || fullApp.surname || "",
          email: applicationPayload.formData?.email || fullApp.email || "",
          phone: applicationPayload.formData?.phone || fullApp.phone_number || "",
          credit: applicationPayload.formData?.credit || fullApp.credit_history || "",
          props: applicationPayload.formData?.props || fullApp.number_of_properties_owned || "",
        },
        btl: {
          ...applicationPayload.btl,
          borrowerType: "Solo",
          solo: {
            firstName: applicationPayload.btl?.solo?.firstName || fullApp.first_name || "",
            surname: applicationPayload.btl?.solo?.surname || fullApp.surname || "",
            email: applicationPayload.btl?.solo?.email || fullApp.email || "",
            phone: applicationPayload.btl?.solo?.phone || fullApp.phone_number || "",
            credit: applicationPayload.btl?.solo?.credit || fullApp.credit_history || "",
            props: applicationPayload.btl?.solo?.props || String(fullApp.number_of_properties_owned || ""),
            agree1: applicationPayload.btl?.solo?.agree1 !== undefined ? applicationPayload.btl.solo.agree1 : (fullApp.provided_information_true === 1),
            agree2: applicationPayload.btl?.solo?.agree2 !== undefined ? applicationPayload.btl.solo.agree2 : (fullApp.property_repossessed === 1),
          },
          solo_uploads: applicationPayload.btl?.solo_uploads || {},
        },
      };
    } else if (borrowerType === "limited" || borrowerType === "ltd co.") {
      applicationPayload = {
        ...applicationPayload, // Preserve existing fields like company_telephone_number, number_of_properties_owned
        formData: {
          company: {
            name: applicationPayload.formData?.company?.name || fullApp.company_name || "",
            regNo: applicationPayload.formData?.company?.regNo || fullApp.company_registration_number || "",
            phone: applicationPayload.formData?.company?.phone || applicationPayload.company_telephone_number || fullApp.company_telephone_number || "",
            email: applicationPayload.formData?.company?.email || fullApp.company_email || "",
          },
          applicants: applicationPayload.formData?.applicants || fullApp.parties || [],
          props: applicationPayload.formData?.props || applicationPayload.number_of_properties_owned || fullApp.number_of_properties_owned || "",
        },
        btl: {
          ...applicationPayload.btl,
          borrowerType: "Ltd Co.",
          limited: {
            company: {
              name: applicationPayload.btl?.limited?.company?.name || fullApp.company_name || "",
              regNo: applicationPayload.btl?.limited?.company?.regNo || fullApp.company_registration_number || "",
              phone: applicationPayload.btl?.limited?.company?.phone || applicationPayload.company_telephone_number || fullApp.company_telephone_number || "",
              email: applicationPayload.btl?.limited?.company?.email || fullApp.company_email || "",
            },
            applicants: applicationPayload.btl?.limited?.applicants || fullApp.parties || [],
            props: applicationPayload.btl?.limited?.props || applicationPayload.number_of_properties_owned || fullApp.number_of_properties_owned || "",
            uploads: applicationPayload.btl?.limited?.uploads || {},
          },
        },
        // Ensure top-level fields are available
        company_telephone_number: applicationPayload.company_telephone_number || fullApp.company_telephone_number || "",
        number_of_properties_owned: applicationPayload.number_of_properties_owned || fullApp.number_of_properties_owned || 0,
      };
    } else if (borrowerType === "partnership") {
      // Convert backend parties array to frontend partners format
      // Backend parties format: { party_role, sequence_no, first_name, surname, email, phone_number, credit_history, number_of_properties_owned }
      // Frontend partners format: { firstName, surname, phone, email, credit, propertyAddress, props }
      let partners = applicationPayload.btl?.partnership?.partners || applicationPayload.formData?.partners || [];
      
      // If partners is empty but we have parties from backend, convert them
      if (partners.length === 0 && fullApp.parties && Array.isArray(fullApp.parties) && fullApp.parties.length > 0) {
        // Get property address and properties owned from backend fields (for first partner)
        const backendPropertyAddress = applicationPayload.btl?.partnership?.propertyAddress || 
          applicationPayload.formData?.propertyAddress || 
          fullApp.property_address || "";
        const backendPropertiesOwned = fullApp.number_of_properties_owned || "";
        
        partners = fullApp.parties.map((party, idx) => ({
          id: party.id || `partner-${idx}`,
          firstName: party.first_name || party.firstName || "",
          surname: party.surname || "",
          phone: party.phone_number || party.phone || "",
          email: party.email || "",
          credit: party.credit_history || party.credit || "",
          // For first partner, use backend fields; for others, use empty
          propertyAddress: idx === 0 ? backendPropertyAddress : "",
          // Properties owned is stored in backend's number_of_properties_owned field (for first partner only)
          // Check both party.number_of_properties_owned and backend document's number_of_properties_owned
          props: idx === 0 ? (String(
            party.number_of_properties_owned || 
            backendPropertiesOwned || 
            applicationPayload.number_of_properties_owned || 
            ""
          )) : "",
        }));
        console.log("[EditApplicationModal] Converted parties to partners:", partners);
        console.log("[EditApplicationModal] Backend properties owned:", backendPropertiesOwned);
      }
      
      applicationPayload = {
        formData: {
          partners: partners,
          propertyAddress: applicationPayload.formData?.propertyAddress || 
            applicationPayload.btl?.partnership?.propertyAddress || 
            fullApp.property_address || "",
        },
        btl: {
          ...applicationPayload.btl,
          borrowerType: "Partnership",
          partnership: {
            partners: partners,
            propertyAddress: applicationPayload.btl?.partnership?.propertyAddress || 
              applicationPayload.formData?.propertyAddress || 
              fullApp.property_address || "",
            uploads: applicationPayload.btl?.partnership?.uploads || {},
          },
        },
      };
    } else if (borrowerType === "pension" || borrowerType === "pension/trust") {
      applicationPayload = {
        ...applicationPayload, // Preserve existing fields like number_of_properties_owned
        formData: {
          pension: {
            trustName: applicationPayload.formData?.pension?.trustName || fullApp.trust_name || "",
            trustNumber: applicationPayload.formData?.pension?.trustNumber || fullApp.trust_number || "",
          },
        },
        btl: {
          ...applicationPayload.btl,
          borrowerType: "Pension/Trust",
          pension: {
            scheme: {
              name: applicationPayload.btl?.pension?.scheme?.name || fullApp.trust_name || "",
              number: applicationPayload.btl?.pension?.scheme?.number || fullApp.trust_number || "",
              props: applicationPayload.btl?.pension?.scheme?.props || applicationPayload.number_of_properties_owned || fullApp.number_of_properties_owned || "",
            },
            members: applicationPayload.btl?.pension?.members || fullApp.parties || [],
            uploads: applicationPayload.btl?.pension?.uploads || {},
          },
        },
        // Ensure top-level fields are available
        number_of_properties_owned: applicationPayload.number_of_properties_owned || fullApp.number_of_properties_owned || 0,
      };
    }
  }

  // State initialization
  const [localFormData, setLocalFormData] = useState(() => {
    // Common property address extraction - check fullApp first, then applicationPayload
    const propertyAddress = fullApp?.property_address ||
      applicationPayload.property_address ||
      applicationPayload.formData?.propertyAddress ||
      applicationPayload.btl?.property_address ||
      applicationPayload.btl?.solo?.propertyAddress ||
      applicationPayload.btl?.limited?.propertyAddress ||
      applicationPayload.btl?.partnership?.propertyAddress ||
      applicationPayload.btl?.pension?.propertyAddress ||
      application?.propertyAddress || // Also check the application object directly
      "";

    if (borrowerType === "individual" || borrowerType === "solo") {
      return {
        firstName: applicationPayload.formData?.firstName || applicationPayload.first_name || "",
        surname: applicationPayload.formData?.surname || applicationPayload.surname || "",
        email: applicationPayload.formData?.email || applicationPayload.email || "",
        phone: applicationPayload.formData?.phone || applicationPayload.phone_number || "",
        credit: applicationPayload.formData?.credit || applicationPayload.credit_history || "",
        props: applicationPayload.formData?.props || applicationPayload.number_of_properties_owned || "",
        propertyAddress
      };
    } else if (borrowerType === "limited" || borrowerType === "ltd co.") {
      let applicants = applicationPayload.formData?.applicants || applicationPayload.btl?.limited?.applicants || [];
      
      // Always check parties from backend first - this is the source of truth
      if (fullApp.parties && Array.isArray(fullApp.parties) && fullApp.parties.length > 0) {
        applicants = fullApp.parties.map((party, idx) => ({
          id: party.name || party.id || `applicant-${idx}`,
          firstName: party.first_name || party.firstName || "",
          surname: party.surname || "",
          phone: party.phone_number || party.phone || "",
          email: party.email || "",
        }));
        console.log("[EditApplicationModal] Converted parties to applicants in state init:", applicants);
      } else if (applicants.length === 0) {
        // Fallback: try to get from applicationPayload
        if (applicationPayload.first_name || applicationPayload.phone_number) {
          applicants = [{
            id: "applicant-0",
            firstName: applicationPayload.first_name || "",
            surname: applicationPayload.surname || "",
            phone: applicationPayload.phone_number || "",
            email: applicationPayload.email || "",
          }];
        }
      }
      
      // Ensure all applicants have proper data
      applicants = applicants.map((app, idx) => ({
        ...app,
        firstName: app.firstName || "",
        phone: app.phone || "",
        surname: app.surname || "",
        email: app.email || "",
      }));
      
      return {
        company: applicationPayload.formData?.company || applicationPayload.btl?.limited?.company || {
          name: applicationPayload.company_name || fullApp.company_name || "",
          regNo: applicationPayload.company_registration_number || fullApp.company_registration_number || "",
          phone: applicationPayload.company_telephone_number || fullApp.company_telephone_number || applicationPayload.formData?.company?.phone || applicationPayload.btl?.limited?.company?.phone || "",
          email: applicationPayload.company_email || fullApp.company_email || ""
        },
        applicants,
        props: String(
          applicationPayload.number_of_properties_owned || 
          fullApp.number_of_properties_owned ||
          applicationPayload.formData?.props || 
          applicationPayload.btl?.limited?.props ||
          ""
        ),
        propertyAddress
      };
    } else if (borrowerType === "partnership") {
      let partners = applicationPayload.formData?.partners || applicationPayload.btl?.partnership?.partners || [];
      
      // Ensure partners is an array
      if (!Array.isArray(partners)) {
        partners = [];
      }
      
      // If no partners but we have parties from backend, convert them
      if (partners.length === 0 && fullApp.parties && Array.isArray(fullApp.parties) && fullApp.parties.length > 0) {
        // Get properties owned from backend document (stored at document level, not in each party)
        const backendPropertiesOwned = fullApp.number_of_properties_owned || 
          applicationPayload.number_of_properties_owned || 
          "";
        
        partners = fullApp.parties.map((party, idx) => ({
          id: party.id || `partner-${idx}`,
          firstName: party.first_name || party.firstName || "",
          surname: party.surname || "",
          phone: party.phone_number || party.phone || "",
          email: party.email || "",
          credit: party.credit_history || party.credit || "",
          propertyAddress: idx === 0 ? propertyAddress : "",
          // For first partner, get props from backend document's number_of_properties_owned
          // For other partners, props should be empty
          props: idx === 0 ? String(party.number_of_properties_owned || backendPropertiesOwned || "") : "",
        }));
        console.log("[EditApplicationModal] Converted parties to partners in state init:", partners);
        console.log("[EditApplicationModal] Backend properties owned:", backendPropertiesOwned);
      }
      
      // Ensure each partner has all required fields
      // Get properties owned from backend if not in partner data (for first partner)
      // This is needed even if partners already exist (they might not have props set)
      const backendPropertiesOwned = fullApp.number_of_properties_owned || 
        applicationPayload.number_of_properties_owned || 
        applicationPayload.formData?.props ||
        "";
      
      partners = partners.map((partner, idx) => {
        // For first partner, get props from multiple sources
        let propsValue = "";
        if (idx === 0) {
          // Priority: partner.props > partner.number_of_properties_owned > backend number_of_properties_owned
          const propsFromPartner = partner.props || partner.number_of_properties_owned;
          propsValue = String(propsFromPartner || backendPropertiesOwned || "").trim();
        } else {
          propsValue = String(partner.props || "").trim();
        }
        
        return {
          id: partner.id || `partner-${idx}`,
          firstName: partner.firstName || partner.first_name || "",
          surname: partner.surname || "",
          phone: partner.phone || partner.phone_number || "",
          email: partner.email || "",
          credit: partner.credit || partner.credit_history || "",
          propertyAddress: partner.propertyAddress || (idx === 0 ? propertyAddress : "") || "",
          props: propsValue,
        };
      });
      
      console.log("[EditApplicationModal] Final partners with props:", partners.map(p => ({ firstName: p.firstName, props: p.props })));
      console.log("[EditApplicationModal] Backend properties owned value:", backendPropertiesOwned);
      
      return {
        partners,
        propertyAddress
      };
    } else if (borrowerType === "pension" || borrowerType === "pension/trust") {
      let members = applicationPayload.formData?.members || applicationPayload.btl?.pension?.members || [];
      
      // Always check parties from backend first - this is the source of truth
      if (fullApp.parties && Array.isArray(fullApp.parties) && fullApp.parties.length > 0) {
        members = fullApp.parties.map((party, idx) => ({
          id: party.name || party.id || `member-${idx}`,
          firstName: party.first_name || party.firstName || "",
          surname: party.surname || "",
          phone: party.phone_number || party.phone || "",
          email: party.email || "",
        }));
        console.log("[EditApplicationModal] Converted parties to members in state init:", members);
      } else if (members.length === 0) {
        // Fallback: try to get from applicationPayload
        if (applicationPayload.first_name || applicationPayload.phone_number) {
          members = [{
            id: "member-0",
            firstName: applicationPayload.first_name || "",
            surname: applicationPayload.surname || "",
            phone: applicationPayload.phone_number || "",
            email: applicationPayload.email || "",
          }];
        }
      }
      
      // Ensure all members have proper data
      members = members.map((m, idx) => ({
        ...m,
        firstName: m.firstName || "",
        phone: m.phone || "",
        surname: m.surname || "",
        email: m.email || "",
      }));
      
      return {
        scheme: applicationPayload.btl?.pension?.scheme || {
          name: applicationPayload.trust_name || fullApp.trust_name || "",
          number: applicationPayload.trust_number || fullApp.trust_number || "",
          props: String(
            applicationPayload.btl?.pension?.scheme?.props ||
            applicationPayload.number_of_properties_owned ||
            fullApp.number_of_properties_owned ||
            applicationPayload.formData?.pension?.props ||
            ""
          )
        },
        members,
        propertyAddress
      };
    }
    return {};
  });

  const digitsOnly = (value) => (value || "").replace(/\D/g, "");
  const phonesInvalid = !arePhonesValid(applicationFormPhones(borrowerType, localFormData));

  const [uploads, setUploads] = useState(() => {
    // Extract uploads from multiple possible locations in applicationPayload
    let extractedUploads = {};

    if (borrowerType === "individual" || borrowerType === "solo") {
      extractedUploads =
        applicationPayload.if?.solo?.uploads ||
        applicationPayload.btl?.solo_uploads ||
        applicationPayload.btl?.solo?.uploads ||
        applicationPayload.formData?.uploads ||
        applicationPayload.uploads ||
        {};
    } else if (borrowerType === "limited" || borrowerType === "ltd co.") {
      extractedUploads =
        applicationPayload.if?.limited?.uploads ||
        applicationPayload.btl?.limited?.uploads ||
        applicationPayload.formData?.uploads ||
        applicationPayload.uploads ||
        {};
    } else if (borrowerType === "partnership") {
      extractedUploads =
        applicationPayload.if?.partnership?.uploads ||
        applicationPayload.btl?.partnership?.uploads ||
        applicationPayload.formData?.uploads ||
        applicationPayload.uploads ||
        {};
    } else if (borrowerType === "pension" || borrowerType === "pension/trust") {
      extractedUploads =
        applicationPayload.if?.pension?.uploads ||
        applicationPayload.btl?.pension?.uploads ||
        applicationPayload.formData?.uploads ||
        applicationPayload.uploads ||
        {};
    }

    if (isIfApp) {
      extractedUploads = normalizeIfUploads(extractedUploads);
    }

    // Log extracted uploads for debugging
    console.log("[EditApplicationModal] Extracted uploads:", {
      borrowerType,
      isIfApp,
      extractedUploads,
      hasUploads: Object.keys(extractedUploads).length > 0,
      uploadKeys: Object.keys(extractedUploads)
    });

    return extractedUploads;
  });

  const uid = () => (typeof crypto !== "undefined" && crypto.randomUUID ? crypto.randomUUID() : `${Date.now()}-${Math.random().toString(16).slice(2)}`);

  // Fetch attached files from backend when modal opens
  useEffect(() => {
    if (!applicationName) return;

    const fetchAttachedFiles = async () => {
      setIsLoadingFiles(true);
      try {
        const docFields = isIfApp ? IF_DOC_FIELDNAMES : BTL_DOC_FIELDNAMES;
        const fieldMapping = isIfApp ? IF_ATTACH_FIELD_MAPPING : BTL_ATTACH_FIELD_MAPPING;

        const appResponse = await api.get(
          `/api/resource/Applications/${encodeURIComponent(applicationName)}`,
          {
            params: {
              fields: JSON.stringify(docFields),
            },
          }
        );

        const appData = appResponse?.data?.data || appResponse?.data?.message || {};
        const fieldFileNames = {};
        docFields.forEach((field) => {
          fieldFileNames[field] = splitDocFieldNames(appData[field]);
        });

        console.log('[EditApplicationModal] File names from backend fields:', fieldFileNames);

        const filters = JSON.stringify([
          ['attached_to_doctype', '=', 'Applications'],
          ['attached_to_name', '=', applicationName],
        ]);

        const response = await api.get('/api/resource/File', {
          params: {
            fields: JSON.stringify(['name', 'file_name', 'file_url', 'attached_to_field', 'is_private']),
            filters,
            limit_page_length: 100,
          },
        });

        const files = response?.data?.data || response?.data?.message?.data || [];
        console.log('[EditApplicationModal] Fetched attached files from backend:', files);

        const filesByField = {};
        const pushFile = (bucketKey, file) => {
          if (!filesByField[bucketKey]) filesByField[bucketKey] = [];
          const fileDisplayName = file.file_name || file.name || file.file_url?.split("/").pop() || "";
          const existingFileNames = new Set(filesByField[bucketKey].map((f) => f.file_name || f.name));
          if (existingFileNames.has(fileDisplayName)) return;
          filesByField[bucketKey].push({
            id: file.name,
            file_url: file.file_url,
            file_name: file.file_name || file.name,
            name: file.name,
            isExisting: true,
          });
        };

        files.forEach((file) => {
          const fieldname = file.attached_to_field || "general";
          const mapping = fieldMapping[fieldname];
          if (!mapping) return;

          const bucketKey = mapping.bucketKey;
          const backendField = mapping.backendField;
          const allowedFileNames = fieldFileNames[backendField] || [];
          const fileName = file.file_name || file.name || file.file_url?.split("/").pop() || "";

          const isInField = isIfApp
            ? fileMatchesDocField(fileName, allowedFileNames)
            : allowedFileNames.some(
                (allowedName) =>
                  fileName === allowedName ||
                  fileName.includes(allowedName) ||
                  allowedName.includes(fileName)
              );

          if (!isInField) {
            console.log(
              `[EditApplicationModal] Skipping file ${fileName} - not in backend field ${backendField}`
            );
            return;
          }

          pushFile(bucketKey, file);
        });

        setUploads((prev) => {
          const prevNormalized = isIfApp ? normalizeIfUploads(prev || {}) : prev || {};
          const newUploads = { ...prevNormalized };

          Object.entries(filesByField).forEach(([bucketKey, bucketFiles]) => {
            if (bucketFiles.length > 0) newUploads[bucketKey] = bucketFiles;
          });

          // Ensure IF doc field string values still surface even if File.attached_to_field is blank/legacy
          if (isIfApp) {
            IF_DOC_FIELDNAMES.forEach((field) => {
              const names = fieldFileNames[field] || [];
              if (!names.length) return;
              const existing = newUploads[field] || [];
              const existingNames = new Set(existing.map((f) => f.file_name || f.name));
              const missing = names.filter((n) => !existingNames.has(n));
              if (!missing.length && existing.length) return;

              // Prefer matching File rows by filename when attached_to_field was wrong/empty
              const matchedFiles = files.filter((file) => {
                const fileName = file.file_name || file.name || file.file_url?.split("/").pop() || "";
                return names.some(
                  (allowedName) =>
                    fileName === allowedName ||
                    fileName.includes(allowedName) ||
                    allowedName.includes(fileName)
                );
              });

              if (matchedFiles.length) {
                matchedFiles.forEach((file) => pushFile(field, file));
                if (filesByField[field]?.length) newUploads[field] = filesByField[field];
              } else if (!existing.length) {
                newUploads[field] = names.map((n) => ({
                  id: n,
                  file_name: n,
                  name: n,
                  isExisting: true,
                }));
              }
            });

            // Recover payload-only IF files (e.g. aged debtor/creditor) by matching File list filenames
            IF_UPLOAD_BUCKETS.forEach((bucket) => {
              const payloadFiles = prevNormalized[bucket.key] || [];
              if (!payloadFiles.length) return;
              if ((newUploads[bucket.key] || []).length) return;

              const payloadNames = payloadFiles
                .map((f) => f.file_name || f.name || (typeof f === "string" ? f : ""))
                .filter(Boolean);

              const matched = files.filter((file) => {
                const fileName = file.file_name || file.name || file.file_url?.split("/").pop() || "";
                return payloadNames.some(
                  (allowedName) =>
                    fileName === allowedName ||
                    fileName.includes(allowedName) ||
                    allowedName.includes(fileName)
                );
              });

              if (matched.length) {
                matched.forEach((file) => pushFile(bucket.key, file));
                newUploads[bucket.key] = filesByField[bucket.key] || matched.map((file) => ({
                  id: file.name,
                  file_url: file.file_url,
                  file_name: file.file_name || file.name,
                  name: file.name,
                  isExisting: true,
                }));
              } else {
                newUploads[bucket.key] = payloadFiles;
              }
            });
          }

          console.log("[EditApplicationModal] Loaded uploads from backend (filtered by field values):", newUploads);
          return newUploads;
        });
      } catch (error) {
        console.error('[EditApplicationModal] Failed to fetch attached files:', error);
      } finally {
        setIsLoadingFiles(false);
      }
    };

    fetchAttachedFiles();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [applicationName, isIfApp]);

  const mapAttachFieldname = (bucketKey, fieldname) => {
    if (isIfApp) {
      if (fieldname === 'app_id') return 'if_id_passport';
      return IF_UPLOAD_FIELD_ALIASES[fieldname] || fieldname;
    }
    const btlMap = {
      borrower_accounts: 'borrower_property_accounts',
      borrower_portfolio: 'borrower_property_portfolio',
      app_assets_liability: 'applicant_assets_liability',
      app_self_employed: 'applicant_self_employed_docs',
      app_employed: 'applicant_employed_docs',
      app_personal_bank: 'applicant_personal_bank_statements',
      app_id: 'applicant_identification',
    };
    return btlMap[fieldname] || fieldname;
  };

  const handleUpload = (bucketKey, fieldname) => async (files) => {
    if (!applicationName) {
      alert('Missing application context.');
      return;
    }
    if (!files?.length) return;
    const attachFieldname = mapAttachFieldname(bucketKey, fieldname);

    try {
      const existingFiles = uploads[bucketKey] || [];
      const newFiles = [];
      const uploadedFileNames = new Set();

      for (const file of files) {
        const uploadId = uid();
        const fileName = file.name || 'Unknown file';

        setUploadingFiles((prev) => ({
          ...prev,
          [bucketKey]: [...(prev[bucketKey] || []), { id: uploadId, name: fileName, progress: 0 }],
        }));

        try {
          const up = await uploadFileToApplication({
            file,
            applicationName,
            fieldname: attachFieldname,
            isPrivate: true,
            onProgress: (percent) => {
              if (typeof percent === 'number' && percent < 100) {
                setUploadingFiles((prev) => ({
                  ...prev,
                  [bucketKey]: (prev[bucketKey] || []).map((u) =>
                    u.id === uploadId ? { ...u, progress: percent } : u
                  ),
                }));
              }
            },
          });

          setUploadingFiles((prev) => ({
            ...prev,
            [bucketKey]: (prev[bucketKey] || []).map((u) =>
              u.id === uploadId ? { ...u, progress: 100 } : u
            ),
          }));

          await new Promise((resolve) => setTimeout(resolve, 300));

          const fileDisplayName = up?.file_name || up?.name || file.name || '';
          if (uploadedFileNames.has(fileDisplayName)) {
            console.log(`[EditApplicationModal] Skipping duplicate upload: ${fileDisplayName}`);
            continue;
          }

          uploadedFileNames.add(fileDisplayName);
          newFiles.push({
            id: uid(),
            file_url: up?.file_url,
            file_name: up?.file_name,
            name: up?.name,
            isNew: true,
            isExisting: false,
          });
        } catch (error) {
          console.error('Upload error:', error);
          alert(`Failed to upload ${fileName}`);
        } finally {
          setTimeout(() => {
            setUploadingFiles((prev) => ({
              ...prev,
              [bucketKey]: (prev[bucketKey] || []).filter((u) => u.id !== uploadId),
            }));
          }, 500);
        }
      }

      const nextUploads = { ...uploads, [bucketKey]: newFiles };
      setUploads(nextUploads);

      console.log(`[EditApplicationModal] Replaced files in ${bucketKey}:`, {
        oldCount: existingFiles.length,
        newCount: newFiles.length,
        newFiles: newFiles.map((f) => f.file_name || f.name),
        attachFieldname,
      });
    } catch (err) {
      console.error('Upload failed', err);
      alert('Failed to upload document.');
    }
  };

  const handleRemove = (bucketKey) => (fileId) => {
    // Remove file from the specific bucket
    const currentBucket = uploads[bucketKey] || [];
    const nextBucket = currentBucket.filter(f => {
      // Match by id, name, or file_url
      return (f.id !== fileId && f.name !== fileId && f.file_url !== fileId);
    });
    const nextUploads = { ...uploads, [bucketKey]: nextBucket };
    setUploads(nextUploads);
    console.log(`[EditApplicationModal] Removed file ${fileId} from ${bucketKey}`, nextBucket);
  };

  const handleSave = async () => {
    try {
      setIsSaving(true);

      let updatedBtlData = { ...(applicationPayload.btl || {}) };
      let updatedIfData = { ...(applicationPayload.if || {}) };

      if (borrowerType === "individual" || borrowerType === "solo") {
        updatedBtlData = {
          ...updatedBtlData,
          solo: {
            ...updatedBtlData.solo,
            ...localFormData,
            uploads,
          },
          solo_uploads: uploads,
        };
        updatedIfData = {
          ...updatedIfData,
          borrowerType: "Solo",
          solo: {
            ...(updatedIfData.solo || {}),
            ...localFormData,
            uploads,
          },
        };
      } else if (borrowerType === "limited" || borrowerType === "ltd co.") {
        updatedBtlData = {
          ...updatedBtlData,
          limited: {
            ...updatedBtlData.limited,
            company: localFormData.company,
            applicants: localFormData.applicants,
            uploads,
          },
        };
        updatedIfData = {
          ...updatedIfData,
          borrowerType: "Ltd Co.",
          limited: {
            ...(updatedIfData.limited || {}),
            company: localFormData.company,
            applicants: localFormData.applicants,
            uploads,
          },
        };
      } else if (borrowerType === "partnership") {
        const propertyAddress = localFormData.partners?.[0]?.propertyAddress || localFormData.propertyAddress || "";
        updatedBtlData = {
          ...updatedBtlData,
          partnership: {
            ...updatedBtlData.partnership,
            partners: localFormData.partners,
            propertyAddress,
            uploads,
          },
        };
        updatedIfData = {
          ...updatedIfData,
          borrowerType: "Partnership",
          partnership: {
            ...(updatedIfData.partnership || {}),
            partners: localFormData.partners,
            propertyAddress,
            uploads,
          },
        };
      } else if (borrowerType === "pension" || borrowerType === "pension/trust") {
        updatedBtlData = {
          ...updatedBtlData,
          pension: {
            ...updatedBtlData.pension,
            scheme: localFormData.scheme,
            members: localFormData.members,
            uploads,
          },
        };
        updatedIfData = {
          ...updatedIfData,
          borrowerType: "Pension/Trust",
          pension: {
            ...(updatedIfData.pension || {}),
            scheme: localFormData.scheme,
            members: localFormData.members,
            uploads,
          },
        };
      }

      const fullPayload = {
        ...applicationPayload,
        formData: localFormData,
        btl: updatedBtlData,
        ...(isIfApp ? { if: updatedIfData } : {}),
      };

      const getFileNames = (uploadArray) => {
        if (!uploadArray || !Array.isArray(uploadArray) || uploadArray.length === 0) {
          return "";
        }
        return uploadArray
          .map((file) => file.file_name || file.name || file.file_url?.split("/").pop())
          .filter(Boolean)
          .join(", ");
      };

      const normalizedUploads = isIfApp ? normalizeIfUploads(uploads || {}) : uploads || {};
      const backendFields = isIfApp
        ? {
            if_full_annual_business_accounts: getFileNames(normalizedUploads.if_full_annual_business_accounts),
            if_management_accounts: getFileNames(normalizedUploads.if_management_accounts),
            if_3_months_business_bank_statement: getFileNames(normalizedUploads.if_3_months_business_bank_statement),
            if_aged_debtors: getFileNames(normalizedUploads.if_aged_debtors),
            if_aged_creditors: getFileNames(normalizedUploads.if_aged_creditors),
            if_id_passport: getFileNames(normalizedUploads.if_id_passport),
          }
        : {
            property_accounts: getFileNames(uploads.borrower_accounts),
            "3_months_bank_statement": getFileNames(uploads.borrower_bank_statements),
            property_portfolio: getFileNames(uploads.borrower_portfolio),
            assets__liability_statement: getFileNames(uploads.app_assets_liability),
            self_employed: getFileNames(uploads.app_self_employed),
            employed: getFileNames(uploads.app_employed),
            "3_months_personal_bank_statements": getFileNames(uploads.app_personal_bank),
            identification: getFileNames(uploads.app_id),
          };

      // Map edited text/data fields to backend field names
      if (borrowerType === "individual" || borrowerType === "solo") {
        Object.assign(backendFields, {
          first_name: localFormData.firstName || "",
          surname: localFormData.surname || "",
          email: localFormData.email || "",
          phone_number: localFormData.phone || "",
          credit_history: localFormData.credit || "",
          ...(isIfApp
            ? {}
            : {
                number_of_properties_owned: Number(localFormData.props) || 0,
                property_address: localFormData.propertyAddress || "",
              }),
        });
      } else if (borrowerType === "limited" || borrowerType === "ltd co.") {
        Object.assign(backendFields, {
          company_name: localFormData.company?.name || "",
          company_registration_number: localFormData.company?.regNo || "",
          company_telephone_number: localFormData.company?.phone || "",
          company_email: localFormData.company?.email || "",
          ...(isIfApp
            ? {}
            : {
                number_of_properties_owned: Number(localFormData.props) || 0,
                property_address: localFormData.propertyAddress || "",
              }),
        });
        if (localFormData.applicants?.length > 0) {
          const first = localFormData.applicants[0];
          Object.assign(backendFields, {
            first_name: first.firstName || "",
            surname: first.surname || "",
            email: first.email || "",
            phone_number: first.phone || "",
          });
          
          // Convert applicants array to parties array format for backend (child table)
          const parties = localFormData.applicants.map((applicant, index) => ({
            party_role: "Director/Shareholder",
            sequence_no: index + 1,
            first_name: applicant.firstName || "",
            surname: applicant.surname || "",
            email: applicant.email || "",
            phone_number: applicant.phone || "",
          }));
          
          // Include parties array in the payload
          Object.assign(backendFields, {
            parties: parties
          });
          
          console.log("[EditApplicationModal] Converting applicants to parties for backend:", parties);
        }
      } else if (borrowerType === "partnership") {
        // Get property address from first partner or main propertyAddress
        const propertyAddress = localFormData.partners?.[0]?.propertyAddress || localFormData.propertyAddress || "";
        
        if (!isIfApp) {
          Object.assign(backendFields, {
            property_address: propertyAddress,
          });
        }
        
        if (localFormData.partners?.length > 0) {
          const first = localFormData.partners[0];
          Object.assign(backendFields, {
            first_name: first.firstName || "",
            surname: first.surname || "",
            email: first.email || "",
            phone_number: first.phone || "",
            credit_history: first.credit || "",
            ...(isIfApp ? {} : { number_of_properties_owned: Number(first.props) || 0 }),
          });
          
          // Convert partners array to parties array format for backend (child table)
          // Backend expects: party_role, sequence_no, first_name, surname, email, phone_number
          const parties = localFormData.partners.map((partner, index) => ({
            party_role: "Partner",
            sequence_no: index + 1,
            first_name: partner.firstName || "",
            surname: partner.surname || "",
            email: partner.email || "",
            phone_number: partner.phone || "",
            ...(index === 0
              ? {
                  credit_history: partner.credit || "",
                  ...(isIfApp ? {} : { number_of_properties_owned: Number(partner.props) || 0 }),
                }
              : {}),
          }));
          
          // Include parties array in the payload
          Object.assign(backendFields, {
            parties: parties
          });
          
          console.log("[EditApplicationModal] Converting partners to parties for backend:", parties);
        }
      } else if (borrowerType === "pension" || borrowerType === "pension/trust") {
        Object.assign(backendFields, {
          trust_name: localFormData.scheme?.name || "",
          trust_number: localFormData.scheme?.number || "",
          ...(isIfApp
            ? {}
            : {
                number_of_properties_owned: Number(localFormData.scheme?.props) || 0,
                property_address: localFormData.propertyAddress || "",
              }),
        });
        if (localFormData.members?.length > 0) {
          const first = localFormData.members[0];
          Object.assign(backendFields, {
            first_name: first.firstName || "",
            surname: first.surname || "",
            email: first.email || "",
            phone_number: first.phone || "",
          });
          
          // Convert members array to parties array format for backend (child table)
          const parties = localFormData.members.map((member, index) => ({
            party_role: "Trustee/Member",
            sequence_no: index + 1,
            first_name: member.firstName || "",
            surname: member.surname || "",
            email: member.email || "",
            phone_number: member.phone || "",
          }));
          
          // Include parties array in the payload
          Object.assign(backendFields, {
            parties: parties
          });
          
          console.log("[EditApplicationModal] Converting members to parties for backend:", parties);
        }
      }

      if (applicationName) {
        // Update both application_payload and backend document fields
        await updateApplication(applicationName, {
          application_payload: JSON.stringify(fullPayload),
          ...backendFields, // Spread backend fields for direct document name updates
        });
        
        // Reload files from backend to ensure UI shows what's actually saved
        try {
          const docFields = isIfApp ? IF_DOC_FIELDNAMES : BTL_DOC_FIELDNAMES;
          const fieldMapping = isIfApp ? IF_ATTACH_FIELD_MAPPING : BTL_ATTACH_FIELD_MAPPING;

          const appResponse = await api.get(`/api/resource/Applications/${encodeURIComponent(applicationName)}`, {
            params: {
              fields: JSON.stringify(docFields),
            },
          });

          const appData = appResponse?.data?.data || appResponse?.data?.message || {};
          const fieldFileNames = {};
          docFields.forEach((field) => {
            fieldFileNames[field] = splitDocFieldNames(appData[field]);
          });

          const filters = JSON.stringify([
            ["attached_to_doctype", "=", "Applications"],
            ["attached_to_name", "=", applicationName],
          ]);

          const response = await api.get("/api/resource/File", {
            params: {
              fields: JSON.stringify(["name", "file_name", "file_url", "attached_to_field", "is_private"]),
              filters,
              limit_page_length: 100,
            },
          });

          const files = response?.data?.data || response?.data?.message?.data || [];
          const filesByField = {};
          files.forEach((file) => {
            const fieldname = file.attached_to_field || "general";
            const mapping = fieldMapping[fieldname];
            if (!mapping) return;

            const bucketKey = mapping.bucketKey;
            const backendField = mapping.backendField;
            const allowedFileNames = fieldFileNames[backendField] || [];
            const fileName = file.file_name || file.name || file.file_url?.split("/").pop() || "";

            const isInField = isIfApp
              ? fileMatchesDocField(fileName, allowedFileNames)
              : allowedFileNames.some(
                  (allowedName) =>
                    fileName === allowedName ||
                    fileName.includes(allowedName) ||
                    allowedName.includes(fileName)
                );

            if (!isInField) {
              console.log(`[EditApplicationModal] Skipping file ${fileName} - not in backend field ${backendField}`);
              return;
            }

            if (!filesByField[bucketKey]) filesByField[bucketKey] = [];
            const fileDisplayName = file.file_name || file.name || file.file_url?.split("/").pop() || "";
            const existingFileNames = new Set(filesByField[bucketKey].map((f) => f.file_name || f.name));
            if (existingFileNames.has(fileDisplayName)) {
              console.log(`[EditApplicationModal] Skipping duplicate file after save: ${fileDisplayName}`);
              return;
            }

            filesByField[bucketKey].push({
              id: file.name,
              file_url: file.file_url,
              file_name: file.file_name || file.name,
              name: file.name,
              isExisting: true,
            });
          });

          const reloadedUploads = {};
          Object.entries(filesByField).forEach(([bucketKey, bucketFiles]) => {
            if (bucketFiles.length > 0) reloadedUploads[bucketKey] = bucketFiles;
          });

          setUploads(reloadedUploads);
          console.log("[EditApplicationModal] Reloaded files from backend after save (filtered by field values):", reloadedUploads);
        } catch (reloadErr) {
          console.warn("[EditApplicationModal] Failed to reload files after save:", reloadErr);
        }

        // Show success confirmation dialog
        setShowSuccessDialog(true);
      }
    } catch (error) {
      console.error("Failed to update application:", error);
      alert("Failed to update application. Please try again.");
    } finally {
      setIsSaving(false);
    }
  };

  const renderUploads = () => {
    if (isLoadingFiles) {
      return (
        <div className="space-y-6 mt-8">
          <h4 className="text-base font-bold text-slate-900 border-b pb-2">Documents & Uploads</h4>
          <div className="flex items-center justify-center py-12">
            <div className="flex flex-col items-center gap-3">
              <Loader2 className="h-8 w-8 text-sky-500 animate-spin" />
              <p className="text-sm text-slate-600">Loading documents...</p>
            </div>
          </div>
        </div>
      );
    }

    if (isIfApp) {
      return (
        <div className="space-y-6 mt-8">
          <h4 className="text-base font-bold text-slate-900 border-b pb-2">Documents & Uploads</h4>
          <p className="text-xs text-slate-600">{UPLOAD_FORMAT_HINT}</p>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            {IF_UPLOAD_BUCKETS.map((bucket) => (
              <SleekUploadTile
                key={bucket.key}
                title={bucket.title}
                sub={UPLOAD_FORMAT_HINT}
                button={bucket.button || (bucket.multiple ? "Select Files" : "Select File")}
                multiple={!!bucket.multiple}
                existingFiles={uploads[bucket.key] || []}
                uploadingFiles={uploadingFiles[bucket.key] || []}
                onFiles={handleUpload(bucket.key, bucket.fieldname)}
                onRemove={handleRemove(bucket.key)}
              />
            ))}
          </div>
        </div>
      );
    }

    return (
      <div className="space-y-6 mt-8">
        <h4 className="text-base font-bold text-slate-900 border-b pb-2">Documents & Uploads</h4>
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          <SleekUploadTile
            title="Property Accounts (if available)"
            sub="PDF, JPG, or PNG up to 10MB"
            existingFiles={uploads.borrower_accounts || []}
            uploadingFiles={uploadingFiles.borrower_accounts || []}
            onFiles={handleUpload("borrower_accounts", "borrower_accounts")}
            onRemove={handleRemove("borrower_accounts")}
          />
          <SleekUploadTile
            title="3 months bank statements"
            sub="PDF, JPG, or PNG up to 10MB total"
            button="Select Files"
            multiple
            existingFiles={uploads.borrower_bank_statements || []}
            uploadingFiles={uploadingFiles.borrower_bank_statements || []}
            onFiles={handleUpload("borrower_bank_statements", "borrower_bank_statements")}
            onRemove={handleRemove("borrower_bank_statements")}
          />
          <SleekUploadTile
            title="Property Portfolio"
            sub="PDF, JPG, or PNG up to 10MB"
            existingFiles={uploads.borrower_portfolio || []}
            uploadingFiles={uploadingFiles.borrower_portfolio || []}
            onFiles={handleUpload("borrower_portfolio", "borrower_portfolio")}
            onRemove={handleRemove("borrower_portfolio")}
          />
          <SleekUploadTile
            title="Assets & Liability Statement"
            sub="PDF, JPG, or PNG up to 10MB"
            existingFiles={uploads.app_assets_liability || []}
            uploadingFiles={uploadingFiles.app_assets_liability || []}
            onFiles={handleUpload("app_assets_liability", "app_assets_liability")}
            onRemove={handleRemove("app_assets_liability")}
          />
          <SleekUploadTile
            title="Self Employed Docs"
            sub="Last 2 years Tax Returns"
            multiple
            existingFiles={uploads.app_self_employed || []}
            uploadingFiles={uploadingFiles.app_self_employed || []}
            onFiles={handleUpload("app_self_employed", "app_self_employed")}
            onRemove={handleRemove("app_self_employed")}
          />
          <SleekUploadTile
            title="Employed Docs"
            sub="P60 and 3 months Payslips"
            multiple
            existingFiles={uploads.app_employed || []}
            uploadingFiles={uploadingFiles.app_employed || []}
            onFiles={handleUpload("app_employed", "app_employed")}
            onRemove={handleRemove("app_employed")}
          />
          <SleekUploadTile
            title="3 months Personal Bank statements"
            sub="PDF, JPG, or PNG up to 10MB total"
            multiple
            existingFiles={uploads.app_personal_bank || []}
            uploadingFiles={uploadingFiles.app_personal_bank || []}
            onFiles={handleUpload("app_personal_bank", "app_personal_bank")}
            onRemove={handleRemove("app_personal_bank")}
          />
          <SleekUploadTile
            title="Identification"
            sub="Passport and/or Driver's Licence"
            multiple
            existingFiles={uploads.app_id || []}
            uploadingFiles={uploadingFiles.app_id || []}
            onFiles={handleUpload("app_id", "app_id")}
            onRemove={handleRemove("app_id")}
          />
        </div>
      </div>
    );
  };

  return (
    <>
      {/* Success Confirmation Dialog */}
      {showSuccessDialog && (
        <div className="fixed inset-0 z-[60] flex items-center justify-center bg-black/50 p-4">
          <div className="bg-white rounded-2xl shadow-xl max-w-md w-full p-6">
            <div className="flex items-center justify-center mb-4">
              <div className="h-16 w-16 rounded-full bg-emerald-100 flex items-center justify-center">
                <CheckCircle className="h-10 w-10 text-emerald-600" />
              </div>
            </div>
            <h3 className="text-xl font-bold text-slate-900 text-center mb-2">Application Updated Successfully!</h3>
            <p className="text-sm text-slate-600 text-center mb-6">
              Your application has been updated and saved to the backend.
            </p>
            <div className="flex gap-3">
              <button
                onClick={() => {
                  setShowSuccessDialog(false);
                  onSave();
                }}
                className="flex-1 px-4 py-2.5 rounded-xl bg-sky-500 text-white font-semibold hover:bg-sky-600 transition-colors"
              >
                OK
              </button>
            </div>
          </div>
        </div>
      )}

      <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/50 p-4">
        <div className="bg-white rounded-2xl shadow-xl max-w-4xl w-full max-h-[90vh] overflow-y-auto">
          <div className="sticky top-0 bg-white border-b border-slate-200 px-6 py-4 flex items-center justify-between z-10">
            <h3 className="text-lg font-bold text-slate-900">Update Application Form</h3>
            <button onClick={onClose} className="text-slate-400 hover:text-slate-600" disabled={isSaving}>
              <X className="h-5 w-5" />
            </button>
          </div>

        <div className="p-6 space-y-8">
          {/* Form Fields Section */}
          <div className="space-y-4">
            <h4 className="text-base font-bold text-slate-900 border-b pb-2">Record Information</h4>
            {(borrowerType === "individual" || borrowerType === "solo") && (
              <>
                <div className="grid grid-cols-2 gap-4">
                  <Field label="First Name">
                    <input type="text" value={localFormData.firstName} onChange={(e) => setLocalFormData({ ...localFormData, firstName: e.target.value })} className="ta-input" disabled={isSaving} />
                  </Field>
                  <Field label="Surname">
                    <input type="text" value={localFormData.surname} onChange={(e) => setLocalFormData({ ...localFormData, surname: e.target.value })} className="ta-input" disabled={isSaving} />
                  </Field>
                </div>
                <div className="grid grid-cols-2 gap-4">
                  <Field label="Email">
                    <input type="email" value={localFormData.email} onChange={(e) => setLocalFormData({ ...localFormData, email: e.target.value })} className="ta-input" disabled={isSaving} />
                  </Field>
                  <Field label="Phone">
                    <PhoneField className="phone-field--ta" value={localFormData.phone} onChange={(next) => setLocalFormData({ ...localFormData, phone: next })} disabled={isSaving} />
                  </Field>
                </div>
                <div className="grid grid-cols-2 gap-4">
                  <Field label="Credit History">
                    <select value={localFormData.credit} onChange={(e) => setLocalFormData({ ...localFormData, credit: e.target.value })} className="ta-input" disabled={isSaving}>
                      <option value="">Select...</option>
                      <option value="Good">Good</option>
                      <option value="Bad">Bad</option>
                      <option value="Poor">Poor</option>
                    </select>
                  </Field>
                  {!isIfApp && (
                    <Field label="Properties Owned">
                      <input type="number" value={localFormData.props} onChange={(e) => setLocalFormData({ ...localFormData, props: e.target.value })} className="ta-input" disabled={isSaving} />
                    </Field>
                  )}
                </div>
                {!isIfApp && (
                  <div className="grid grid-cols-1 gap-4">
                    <Field label="Security Address">
                      <input type="text" value={localFormData.propertyAddress} onChange={(e) => setLocalFormData({ ...localFormData, propertyAddress: e.target.value })} className="ta-input" placeholder="Enter security address..." disabled={isSaving} />
                    </Field>
                  </div>
                )}
              </>
            )}

            {(borrowerType === "limited" || borrowerType === "ltd co.") && (
              <>
                <div className="grid grid-cols-2 gap-4">
                  <Field label="Company Name">
                    <input type="text" value={localFormData.company.name} onChange={(e) => setLocalFormData({ ...localFormData, company: { ...localFormData.company, name: e.target.value } })} className="ta-input" disabled={isSaving} />
                  </Field>
                  <Field label="Registration Number">
                    <input type="text" inputMode="numeric" value={localFormData.company.regNo} onChange={(e) => setLocalFormData({ ...localFormData, company: { ...localFormData.company, regNo: digitsOnly(e.target.value) } })} className="ta-input" disabled={isSaving} />
                  </Field>
                </div>
                <div className="grid grid-cols-2 gap-4">
                  <Field label="Company Phone">
                    <PhoneField className="phone-field--ta" value={localFormData.company?.phone || ""} mobileOnly={false} onChange={(next) => setLocalFormData({ ...localFormData, company: { ...localFormData.company, phone: next } })} disabled={isSaving} />
                  </Field>
                  <Field label="Company Email">
                    <input type="email" value={localFormData.company?.email || ""} onChange={(e) => setLocalFormData({ ...localFormData, company: { ...localFormData.company, email: e.target.value } })} className="ta-input" disabled={isSaving} />
                  </Field>
                </div>
                {!isIfApp && (
                  <div className="grid grid-cols-2 gap-4">
                    <Field label="Properties Owned">
                      <input type="number" value={localFormData.props || ""} onChange={(e) => setLocalFormData({ ...localFormData, props: e.target.value })} className="ta-input" disabled={isSaving} />
                    </Field>
                    <Field label="Security Address">
                      <input type="text" value={localFormData.propertyAddress || ""} onChange={(e) => setLocalFormData({ ...localFormData, propertyAddress: e.target.value })} className="ta-input" placeholder="Enter security address..." disabled={isSaving} />
                    </Field>
                  </div>
                )}

                {localFormData.applicants?.length > 0 && (
                  <div className="mt-4 space-y-4">
                    <h5 className="text-sm font-bold text-slate-700">Applicant Information</h5>
                    <p className="text-xs text-slate-600 mb-3">(Must be 20% shareholder of the company)</p>
                    {localFormData.applicants.map((app, idx) => (
                      <div key={app.id || idx} className="p-4 border border-slate-200 rounded-xl bg-slate-50">
                        <div className="mb-3 flex items-center justify-between">
                          <div className="text-sm font-semibold text-slate-700">Applicant {idx + 1}</div>
                          {idx > 0 && (
                            <button
                              type="button"
                              onClick={() => {
                                if (localFormData.applicants.length > 1) {
                                  const updated = localFormData.applicants.filter((_, i) => i !== idx);
                                  setLocalFormData({ ...localFormData, applicants: updated });
                                }
                              }}
                              className="inline-flex h-8 w-8 items-center justify-center rounded-lg border border-red-200 text-red-500 hover:bg-red-50 transition-colors"
                              disabled={isSaving || localFormData.applicants.length <= 1}
                              title="Remove this applicant"
                            >
                              <span className="text-lg">×</span>
                            </button>
                          )}
                        </div>
                        <div className="grid grid-cols-2 gap-4">
                          <Field label="First Name">
                            <input type="text" value={app.firstName || ""} onChange={(e) => {
                              const updated = [...localFormData.applicants];
                              updated[idx] = { ...updated[idx], firstName: e.target.value };
                              setLocalFormData({ ...localFormData, applicants: updated });
                            }} className="ta-input bg-white" disabled={isSaving} />
                          </Field>
                          <Field label="Surname">
                            <input type="text" value={app.surname || ""} onChange={(e) => {
                              const updated = [...localFormData.applicants];
                              updated[idx] = { ...updated[idx], surname: e.target.value };
                              setLocalFormData({ ...localFormData, applicants: updated });
                            }} className="ta-input bg-white" disabled={isSaving} />
                          </Field>
                        </div>
                        <div className="grid grid-cols-2 gap-4 mt-4">
                          <Field label="Phone Number">
                            <PhoneField className="phone-field--ta" value={app.phone || ""} required={idx === 0} onChange={(next) => {
                              const updated = [...localFormData.applicants];
                              updated[idx] = { ...updated[idx], phone: next };
                              setLocalFormData({ ...localFormData, applicants: updated });
                            }} disabled={isSaving} />
                          </Field>
                          <Field label="Email">
                            <input type="email" value={app.email || ""} onChange={(e) => {
                              const updated = [...localFormData.applicants];
                              updated[idx] = { ...updated[idx], email: e.target.value };
                              setLocalFormData({ ...localFormData, applicants: updated });
                            }} className="ta-input bg-white" disabled={isSaving} />
                          </Field>
                        </div>
                      </div>
                    ))}
                  </div>
                )}
              </>
            )}

            {borrowerType === "partnership" && (
              <div className="space-y-4">
                <h5 className="text-sm font-bold text-slate-700">Partners</h5>
                {localFormData.partners?.map((p, idx) => (
                  <div key={p.id || idx} className="p-4 border border-slate-200 rounded-xl bg-slate-50">
                    <div className="mb-4 flex items-center justify-between border-b border-slate-200 pb-2">
                      <div className="text-base font-bold text-slate-900">
                        {idx === 0 ? "Main Partner" : `Partner ${idx + 1}`}
                      </div>
                      {localFormData.partners.length > 1 && idx > 1 && (
                        <button
                          type="button"
                          onClick={() => {
                            if (localFormData.partners.length > 1) {
                              const updated = localFormData.partners.filter((_, i) => i !== idx);
                              setLocalFormData({ ...localFormData, partners: updated });
                            }
                          }}
                          className="inline-flex h-8 w-8 items-center justify-center rounded-lg border border-red-200 text-red-500 hover:bg-red-50 transition-colors"
                          disabled={isSaving || localFormData.partners.length <= 1}
                          title="Remove this partner"
                        >
                          <span className="text-lg">×</span>
                        </button>
                      )}
                    </div>
                    <div className="grid grid-cols-2 gap-4">
                      <Field label="First Name">
                        <input type="text" value={p.firstName || ""} onChange={(e) => {
                          const updated = [...localFormData.partners];
                          updated[idx] = { ...updated[idx], firstName: e.target.value };
                          setLocalFormData({ ...localFormData, partners: updated });
                        }} className="ta-input bg-white" disabled={isSaving} />
                      </Field>
                      <Field label="Surname">
                        <input type="text" value={p.surname || ""} onChange={(e) => {
                          const updated = [...localFormData.partners];
                          updated[idx] = { ...updated[idx], surname: e.target.value };
                          setLocalFormData({ ...localFormData, partners: updated });
                        }} className="ta-input bg-white" disabled={isSaving} />
                      </Field>
                    </div>
                    <div className="grid grid-cols-2 gap-4 mt-4">
                      <Field label="Phone">
                        <PhoneField className="phone-field--ta" value={p.phone || ""} required={idx === 0} onChange={(next) => {
                          const updated = [...localFormData.partners];
                          updated[idx] = { ...updated[idx], phone: next };
                          setLocalFormData({ ...localFormData, partners: updated });
                        }} disabled={isSaving} />
                      </Field>
                      <Field label="Email">
                        <input type="email" value={p.email || ""} onChange={(e) => {
                          const updated = [...localFormData.partners];
                          updated[idx] = { ...updated[idx], email: e.target.value };
                          setLocalFormData({ ...localFormData, partners: updated });
                        }} className="ta-input bg-white" disabled={isSaving} />
                      </Field>
                    </div>
                    {/* Only show credit history, property address, and properties owned for the first partner (Main Partner) */}
                    {idx === 0 && (
                      <>
                        <div className="grid grid-cols-1 gap-4 mt-4">
                          <Field label="Credit History">
                            <select value={p.credit || ""} onChange={(e) => {
                              const updated = [...localFormData.partners];
                              updated[idx] = { ...updated[idx], credit: e.target.value };
                              setLocalFormData({ ...localFormData, partners: updated });
                            }} className="ta-input bg-white" disabled={isSaving}>
                              <option value="">Select...</option>
                              <option value="Good">Good</option>
                              <option value="Bad">Bad</option>
                              <option value="Poor">Poor</option>
                            </select>
                          </Field>
                        </div>
                        {!isIfApp && (
                          <div className="grid grid-cols-2 gap-4 mt-4">
                            <Field label="Security Address">
                              <input type="text" value={p.propertyAddress || localFormData.propertyAddress || ""} onChange={(e) => {
                                const updated = [...localFormData.partners];
                                updated[idx] = { ...updated[idx], propertyAddress: e.target.value };
                                setLocalFormData({ ...localFormData, partners: updated, propertyAddress: e.target.value });
                              }} className="ta-input bg-white" placeholder="Enter security address..." disabled={isSaving} />
                            </Field>
                            <Field label="Properties Owned">
                              <input type="number" value={p.props || ""} onChange={(e) => {
                                const updated = [...localFormData.partners];
                                updated[idx] = { ...updated[idx], props: e.target.value };
                                setLocalFormData({ ...localFormData, partners: updated });
                              }} className="ta-input bg-white" disabled={isSaving} />
                            </Field>
                          </div>
                        )}
                      </>
                    )}
                  </div>
                ))}
              </div>
            )}

            {(borrowerType === "pension" || borrowerType === "pension/trust") && (
              <>
                <div className="grid grid-cols-2 gap-4">
                  <Field label="Pension/Trust Name">
                    <input type="text" value={localFormData.scheme.name || ""} onChange={(e) => setLocalFormData({ ...localFormData, scheme: { ...localFormData.scheme, name: e.target.value } })} className="ta-input" disabled={isSaving} />
                  </Field>
                  <Field label="Pension/Trust Number">
                    <input type="text" value={localFormData.scheme.number || ""} onChange={(e) => setLocalFormData({ ...localFormData, scheme: { ...localFormData.scheme, number: e.target.value } })} className="ta-input" disabled={isSaving} />
                  </Field>
                </div>
                {!isIfApp && (
                  <>
                    <div className="grid grid-cols-2 gap-4">
                      <Field label="Properties Owned">
                        <input type="number" value={localFormData.scheme.props || ""} onChange={(e) => setLocalFormData({ ...localFormData, scheme: { ...localFormData.scheme, props: e.target.value } })} className="ta-input" disabled={isSaving} />
                      </Field>
                    </div>
                    <div className="grid grid-cols-1 gap-4">
                      <Field label="Security Address">
                        <input type="text" value={localFormData.propertyAddress || ""} onChange={(e) => setLocalFormData({ ...localFormData, propertyAddress: e.target.value })} className="ta-input" placeholder="Enter security address..." disabled={isSaving} />
                      </Field>
                    </div>
                  </>
                )}
                {localFormData.members?.length > 0 && (
                  <div className="mt-4 space-y-4">
                    <h5 className="text-sm font-bold text-slate-700">Members</h5>
                    {localFormData.members.map((m, idx) => (
                      <div key={m.id || idx} className="p-4 border border-slate-200 rounded-xl bg-slate-50">
                        <div className="mb-3 flex items-center justify-between">
                          <div className="text-sm font-semibold text-slate-700">Member {idx + 1}</div>
                          {idx > 0 && (
                            <button
                              type="button"
                              onClick={() => {
                                if (localFormData.members.length > 1) {
                                  const updated = localFormData.members.filter((_, i) => i !== idx);
                                  setLocalFormData({ ...localFormData, members: updated });
                                }
                              }}
                              className="inline-flex h-8 w-8 items-center justify-center rounded-lg border border-red-200 text-red-500 hover:bg-red-50 transition-colors"
                              disabled={isSaving || localFormData.members.length <= 1}
                              title="Remove this member"
                            >
                              <span className="text-lg">×</span>
                            </button>
                          )}
                        </div>
                        <div className="grid grid-cols-2 gap-4">
                          <Field label="First Name">
                            <input type="text" value={m.firstName || ""} onChange={(e) => {
                              const updated = [...localFormData.members];
                              updated[idx] = { ...updated[idx], firstName: e.target.value };
                              setLocalFormData({ ...localFormData, members: updated });
                            }} className="ta-input bg-white" disabled={isSaving} />
                          </Field>
                          <Field label="Surname">
                            <input type="text" value={m.surname || ""} onChange={(e) => {
                              const updated = [...localFormData.members];
                              updated[idx] = { ...updated[idx], surname: e.target.value };
                              setLocalFormData({ ...localFormData, members: updated });
                            }} className="ta-input bg-white" disabled={isSaving} />
                          </Field>
                        </div>
                        <div className="grid grid-cols-2 gap-4 mt-4">
                          <Field label="Phone Number">
                            <PhoneField className="phone-field--ta" value={m.phone || ""} required={idx === 0} onChange={(next) => {
                              const updated = [...localFormData.members];
                              updated[idx] = { ...updated[idx], phone: next };
                              setLocalFormData({ ...localFormData, members: updated });
                            }} disabled={isSaving} />
                          </Field>
                          <Field label="Email">
                            <input type="email" value={m.email || ""} onChange={(e) => {
                              const updated = [...localFormData.members];
                              updated[idx] = { ...updated[idx], email: e.target.value };
                              setLocalFormData({ ...localFormData, members: updated });
                            }} className="ta-input bg-white" disabled={isSaving} />
                          </Field>
                        </div>
                      </div>
                    ))}
                  </div>
                )}
              </>
            )}
          </div>

          {/* Uploads Section */}
          {renderUploads()}
        </div>

        <div className="sticky bottom-0 bg-slate-50 border-t border-slate-200 px-6 py-4 flex items-center justify-end gap-3 z-10">
          <button onClick={onClose} className="px-6 py-2.5 text-sm font-bold border border-slate-300 rounded-xl hover:bg-white transition-colors" disabled={isSaving}>
            Cancel
          </button>
          <button onClick={handleSave} className="px-6 py-2.5 text-sm font-bold text-white bg-sky-600 rounded-xl hover:bg-sky-700 disabled:opacity-70 flex items-center gap-2 shadow-lg shadow-sky-200 transition-all active:scale-95" disabled={isSaving || phonesInvalid}>
            {isSaving ? (
              <>
                <Loader2 className="h-4 w-4 animate-spin" />
                Updating...
              </>
            ) : "Save Changes"}
          </button>
        </div>
      </div>
    </div>
    </>
  );
}

/* -----------------------------
   CSS (single-file)
----------------------------- */

const css = `
:root{
  --bg: #f8fafc;
  --card: #ffffff;
  --text: #0f172a;
  --muted: #64748b;
  --line: #e7eaef;
  --shadow: none;
  --radius: 16px;
  --radius2: 14px;
  --btnRadius: 12px;
  --accent: #1aa0db;

  --blue1:#0ea5e9;
  --blue2:#0b77c5;
  --teal1:#09b0c2;
  --teal2:#008aa7;
  --indigo1:#6366f1;
  --indigo2:#3b82f6;
  --mag1:#d946ef;
  --mag2:#a855f7;

  --green:#16a34a;
  --amber:#d97706;
  --cyan:#1aa0db;
  --pending:#cbd5e1;

  --pillGreenBg:#ecfdf3;
  --pillGreenText:#15803d;

  --pillInProgressBg:#16a34a;
  --pillInProgressText:#ffffff;

  --pillBlueBg:#e8f6fc;
  --pillBlueText:#0369a1;

  --pillPendingBg:#f1f5f9;
  --pillPendingText:#64748b;
}

*{ box-sizing:border-box; }
.ta-page{
  min-height:100vh;
  background: var(--bg);
  padding: 22px 16px 60px;
  color: var(--text);
  font-family: ui-sans-serif, system-ui, -apple-system, Segoe UI, Roboto, Helvetica, Arial, "Apple Color Emoji","Segoe UI Emoji";
}
.ta-shell{
  max-width: 1120px;
  margin: 0 auto;
}
.ta-topbar{
  display:flex;
  align-items:flex-end;
  justify-content:space-between;
  gap:16px;
  margin-bottom: 14px;
}
.ta-selectWrap{ flex:1; }
.ta-label{
  display:block;
  font-size:12px;
  color: var(--muted);
  margin: 0 0 6px;
}
.ta-selectRow{
  display:flex;
  gap:12px;
  align-items:center;
  flex-wrap:wrap;
}
.ta-select{
  appearance:none;
  border:1px solid var(--line);
  background: #fff;
  border-radius: 14px;
  padding: 10px 12px;
  min-width: 320px;
  font-weight: 600;
  color: var(--text);
  box-shadow: 0 1px 0 rgba(15,23,42,.02);
}
.ta-actions{ display:flex; gap:10px; }
.ta-mutedRight{
  display:flex;
  align-items:center;
  justify-content:flex-end;
}
.ta-clock{
  display:flex;
  align-items:center;
  gap:8px;
  color: var(--muted);
  font-size: 12px;
  white-space:nowrap;
}

.ta-card{
  background: var(--card);
  border-radius: var(--radius);
  box-shadow: none;
  border: 1px solid var(--line);
  padding: 22px;
  margin-top: 14px;
}
.ta-cardHeader{
  display:flex;
  align-items:flex-start;
  justify-content:space-between;
  gap: 10px;
  margin-bottom: 16px;
}
.ta-title{
  margin:0;
  font-size: 22px;
  font-weight: 700;
  color: var(--text);
  letter-spacing: -0.02em;
}
.ta-statusPill{
  display:inline-flex;
  align-items:center;
  padding: 4px 10px;
  border-radius: 999px;
  font-size: 11px;
  font-weight: 700;
  border: 1px solid transparent;
}
.ta-statusPill--approved{
  background: var(--pillGreenBg);
  color: var(--pillGreenText);
  border-color: #bbf7d0;
}
.ta-statusPill--inprogress{
  background: var(--pillInProgressBg);
  color: var(--pillInProgressText);
  border-color: var(--pillInProgressBg);
}
.ta-appIdValue{
  font-weight: 600;
  color: var(--muted);
  font-size: 13px;
  margin-top: 6px;
}
.ta-appIdValue--mono{
  font-family: ui-monospace, SFMono-Regular, Menlo, Monaco, Consolas, monospace;
  letter-spacing: 0.02em;
}

.ta-factsGrid{
  display:grid;
  grid-template-columns: repeat(2, minmax(0, 1fr));
  gap: 10px;
  margin-bottom: 16px;
}
.ta-factTile{
  border: 1px solid var(--line);
  border-radius: 12px;
  background: #fff;
  padding: 12px 14px;
}
.ta-factLabel{
  font-size: 11px;
  color: var(--muted);
  font-weight: 600;
  margin-bottom: 4px;
  text-transform: capitalize;
}
.ta-factValue{
  font-size: 16px;
  font-weight: 700;
  color: var(--text);
  letter-spacing: -0.01em;
}

.ta-kpiProgressSection{
  display: flex;
  flex-direction: column;
}

.ta-kpis{
  display:grid;
  grid-template-columns: repeat(4, minmax(0, 1fr));
  gap: 14px;
  margin-bottom: 16px;
  order: 1;
}
.ta-kpi{
  border-radius: 14px;
  padding: 14px 16px;
  color: #fff;
  position:relative;
  overflow:hidden;
  cursor: pointer;
  transition: all 0.3s ease;
  transform: translateY(0);
}
.ta-kpi:hover{
  transform: translateY(-4px) scale(1.02);
  box-shadow: 0 12px 24px rgba(0, 0, 0, 0.15);
}
.ta-kpi:before{
  content:"";
  position:absolute;
  inset:-40px -60px auto auto;
  width: 180px;
  height: 180px;
  border-radius: 999px;
  background: rgba(255,255,255,.12);
  transform: rotate(22deg);
  transition: all 0.3s ease;
  pointer-events: none;
}
.ta-kpi:hover:before{
  transform: rotate(22deg) scale(1.1);
  background: rgba(255,255,255,.18);
}
.ta-kpiLabel{
  position:relative;
  font-size: 12px;
  opacity: .95;
  margin-bottom: 6px;
}
.ta-kpiValue{
  position:relative;
  font-size: 20px;
  font-weight: 800;
  letter-spacing: -0.01em;
}

.ta-kpi-blue{ background: linear-gradient(90deg, var(--blue2), var(--blue1)); }
.ta-kpi-teal{ background: linear-gradient(90deg, var(--teal2), var(--teal1)); }
.ta-kpi-indigo{ background: linear-gradient(90deg, #3f3fe3, #6b73ff); }
.ta-kpi-magenta{ background: linear-gradient(90deg, #b832f0, #e052ff); }

.ta-progressBlock{
  margin: 6px 0 14px;
  order: 2;
}
.ta-progressLabelRow{
  display:flex;
  align-items:center;
  justify-content:space-between;
  margin-bottom: 8px;
}
.ta-progressLabel{
  color: #334155;
  font-weight: 700;
  font-size: 13px;
}
.ta-progressPct{
  color:#334155;
  font-weight: 800;
  font-size: 13px;
}
.ta-progressTrack{
  height: 10px;
  border-radius: 999px;
  background: #e8edf5;
  overflow:hidden;
}
.ta-progressFill{
  height: 100%;
  border-radius: 999px;
  background: linear-gradient(90deg, var(--blue2), var(--blue1));
  box-shadow: 0 4px 12px rgba(14, 165, 233, .2);
}

.ta-illustration{
  border: 2px solid #bae6fd;
  background: #f0f9ff;
  border-radius: var(--radius2);
  padding: 16px;
  display:flex;
  align-items:center;
  justify-content:space-between;
  gap: 14px;
  flex-wrap: wrap;
}
.ta-illusIcon{
  width: 44px;
  height: 44px;
  border-radius: 12px;
  background: #0ea5e9;
  color:#fff;
  display:flex;
  align-items:center;
  justify-content:center;
  flex: 0 0 auto;
}
.ta-illusTitle{
  font-weight: 800;
  color:#0f172a;
  line-height: 1.1;
}
.ta-illusSub{
  color: var(--muted);
  font-size: 12px;
  margin-top: 2px;
}

.ta-manager{
  margin-top: 14px;
  border-radius: var(--radius2);
  padding: 14px 16px;
  display:flex;
  align-items:center;
  justify-content:space-between;
  gap: 14px;
}
.ta-manager--case{
  background: #eff6ff;
  border: 1px solid #bfdbfe;
}
.ta-manager--enquiries{
  background: #fef3c7;
  border: 1px solid #facc15;
}
.ta-avatar{
  width: 44px;
  height: 44px;
  border-radius: 999px;
  background: #0ea5e9;
  color:#fff;
  display:flex;
  align-items:center;
  justify-content:center;
  font-weight: 800;
  letter-spacing: .06em;
}
.ta-managerLeft{
  display:flex;
  align-items:center;
  gap: 12px;
}
.ta-illusLeft{
  display:flex;
  align-items:center;
  gap: 12px;
  min-width: 0;
  flex: 1 1 auto;
}
.ta-managerLabel{
  color: #64748b;
  font-size: 12px;
}
.ta-managerName{
  font-weight: 900;
  letter-spacing: -0.01em;
}
.ta-managerRight{
  display:flex;
  align-items:center;
  gap: 18px;
  flex-wrap:wrap;
  justify-content:flex-end;
}
.ta-link{
  display:flex;
  align-items:center;
  gap: 8px;
  color:#334155;
  text-decoration:none;
  font-weight: 700;
  font-size: 13px;
}
.ta-link:hover{ text-decoration: underline; }

.ta-cardActivity{
  padding-top: 18px;
  overflow: visible;
}
.ta-activityHeader{
  display:flex;
  align-items:center;
  justify-content:space-between;
  margin-bottom: 12px;
}
.ta-activityTitle{
  font-size: 18px;
  font-weight: 700;
  color: var(--text);
  letter-spacing: -0.02em;
}

.ta-timelineWrap{
  position:relative;
  padding: 8px 0 12px;
  overflow: visible;
}
.ta-timelineLine{
  position:absolute;
  left: 15px;
  top: 12px;
  bottom: 12px;
  width: 2px;
  border-radius: 999px;
  background: var(--line);
  z-index: 0;
}
.ta-steps{
  list-style: none;
  margin: 0;
  padding: 0;
  display: flex;
  flex-direction: column;
  gap: 0;
  position: relative;
  min-width: 0;
}
.ta-step-new{
  display:flex;
  flex-direction: row;
  align-items: flex-start;
  text-align: left;
  gap: 14px;
  padding: 12px 0;
  position: relative;
  z-index: 1;
}
.ta-step-new:first-child{ padding-top: 0; }
.ta-stepNodeWrap{
  flex-shrink: 0;
  width: 32px;
  display: flex;
  justify-content: center;
}
.ta-stepCircleWrap{
  position: relative;
  display: inline-block;
}
.ta-stageInfoBtnCircle{
  position: absolute;
  top: -2px;
  right: -10px;
  width: 16px;
  height: 16px;
  border-radius: 50%;
  background-color: #dbeafe;
  color: #3b82f6;
  border: 1.5px solid #3b82f6;
  display: flex;
  align-items: center;
  justify-content: center;
  cursor: pointer;
  padding: 0;
  z-index: 3;
  box-shadow: 0 1px 3px rgba(0, 0, 0, 0.1);
  transition: transform 0.2s ease;
}
.ta-stageInfoBtnCircle .ta-stageInfoIcon{
  width: 10px;
  height: 10px;
}
.ta-stageInfoBtnCircle:hover{
  transform: scale(1.1);
}
.ta-stageInfoPopup{
  background-color: #ffffff;
  border: 2px solid #38bdf8;
  border-radius: 8px;
  padding: 8px 12px;
  padding-top: 28px;
  padding-bottom: 10px;
  font-size: 12px;
  line-height: 1.4;
  color: #000000;
  box-shadow: 0 4px 12px rgba(0, 0, 0, 0.15);
  pointer-events: auto;
  white-space: normal;
  word-wrap: break-word;
}
.ta-stageInfoPopupTitle{
  font-weight: 600;
  margin-bottom: 6px;
  color: #000000;
  font-size: 13px;
  line-height: 1.4;
}
.ta-stageInfoPopupText{
  color: #000000;
  font-size: 12px;
  line-height: 1.4;
  opacity: 0.9;
}
.ta-stageInfoPopupClose{
  position: absolute;
  top: 8px;
  right: 8px;
  background: transparent;
  border: none;
  cursor: pointer;
  padding: 4px;
  display: flex;
  align-items: center;
  justify-content: center;
  color: #64748b;
  opacity: 0.7;
  z-index: 10000;
}
.ta-stageInfoPopupClose:hover{
  opacity: 1;
  color: #475569;
}
.ta-stageInfoBackdrop{
  position: fixed;
  inset: 0;
  z-index: 9999;
  background: transparent;
}

@media (max-width: 767px){
  .ta-stageInfoBtnCircle{
    width: 18px;
    height: 18px;
    right: -12px;
    top: -3px;
  }
  .ta-stageInfoBtnCircle::before{
    content: "";
    position: absolute;
    inset: -12px;
  }
  .ta-stageInfoBtnCircle .ta-stageInfoIcon{
    width: 11px;
    height: 11px;
  }
  .ta-stageInfoPopup{
    box-shadow: 0 8px 24px rgba(15, 23, 42, 0.18);
    padding: 8px 10px;
    padding-top: 24px;
    font-size: 11px;
    line-height: 1.35;
  }
  .ta-stageInfoPopupTitle{
    font-size: 12px;
    margin-bottom: 4px;
  }
  .ta-stageInfoPopupText{
    font-size: 11px;
  }
  .ta-stageInfoPopupClose{
    top: 4px;
    right: 4px;
    padding: 4px;
  }
}
.ta-stepCircle-v2{
  width: 32px;
  height: 32px;
  border-radius: 50%;
  display:flex;
  align-items:center;
  justify-content:center;
  position:relative;
  z-index: 2;
  background: #fff;
}
.ta-stepCircle-v2.is-done{
  background: #14b8a6;
  color: #fff;
  border: 2px solid #14b8a6;
}
.ta-stepCircle-v2.is-current{
  background: var(--accent);
  color: #fff;
  border: 2px solid var(--accent);
}
.ta-stepCircle-v2.is-pending{
  background: #fff;
  color: var(--muted);
  border: 2px solid #cbd5e1;
}
.ta-stepContent-new{
  flex: 1;
  min-width: 0;
  display:flex;
  flex-direction:column;
  align-items:flex-start;
  gap: 4px;
}
.ta-stepTitle-new{
  font-weight: 600;
  color: var(--text);
  font-size: 14px;
  margin-top: 0;
}
.ta-stepMeta{
  font-size: 11px;
  color: var(--muted);
  display: inline-flex;
  align-items: center;
  gap: 4px;
  line-height: 1.3;
}
.ta-stepMetaIcon{
  width: 12px;
  height: 12px;
  flex-shrink: 0;
  opacity: 0.85;
}
.ta-pill{
  font-size: 11px;
  font-weight: 700;
  padding: 4px 10px;
  border-radius: 999px;
  display:inline-flex;
  align-items:center;
  justify-content:center;
  border: 1px solid transparent;
}
.ta-pill-completed{ background: var(--pillGreenBg); color: var(--pillGreenText); border-color: #bbf7d0; }
.ta-pill-inprogress{ background: var(--pillInProgressBg); color: var(--pillInProgressText); border-color: var(--pillInProgressBg); }
.ta-pill-pending{ background: var(--pillPendingBg); color: var(--pillPendingText); border-color: var(--line); }

/* Desktop: horizontal activity timeline */
@media (min-width: 768px){
  .ta-timelineWrap{
    padding: 20px 8px 24px;
    overflow-x: auto;
  }
  .ta-timelineLine{
    left: 24px;
    right: 24px;
    top: 28px;
    bottom: auto;
    width: auto;
    height: 2px;
  }
  .ta-steps{
    display: grid;
    grid-auto-flow: column;
    grid-auto-columns: minmax(128px, 1fr);
    gap: 0;
    padding: 0 10px;
    min-width: min(100%, 100%);
  }
  .ta-step-new{
    flex-direction: column;
    align-items: center;
    text-align: center;
    gap: 6px;
    padding: 0 4px;
  }
  .ta-step-new:first-child{ padding-top: 0; }
  .ta-stepNodeWrap{
    width: auto;
  }
  .ta-stepContent-new{
    flex: none;
    width: 100%;
    align-items: center;
  }
  .ta-stepTitle-new{
    font-size: 13px;
    line-height: 1.25;
  }
  .ta-stepMeta{
    min-height: 16px;
  }
}

/* Legacy horizontal timeline — unused */
.ta-step-new-legacy{
  display:flex;
  flex-direction:column;
  align-items:center;
  text-align:center;
  gap: 6px;
}
.ta-stepDot{
  width: 40px;
  height: 40px;
  border-radius: 999px;
  display:flex;
  align-items:center;
  justify-content:center;
  border: 3px solid #fff;
  box-shadow: 0 10px 18px rgba(15,23,42,.10);
  z-index: 2;
}
.ta-dot-completed{ background: var(--green); color:#fff; }
.ta-dot-action{ background: var(--amber); color:#fff; }
.ta-dot-inprogress{ background: var(--cyan); color:#fff; }
.ta-dot-pending{
  background: #fff;
  border: 2px solid #e5eaf2;
  box-shadow: none;
  color: #94a3b8;
}
.ta-dotEmpty{
  width: 12px;
  height: 12px;
  border-radius: 999px;
  background: #cbd5e1;
  display:block;
}
.ta-stepTitle{
  font-weight: 900;
  color:#0f172a;
  font-size: 13px;
}
.ta-stepMeta{
  font-size: 11px;
  color: var(--muted);
  min-height: 16px;
}
.ta-stepTitle{
  font-weight: 700;
  color:#0f172a;
  font-size: 13px;
}

.ta-btn{
  border: 0;
  border-radius: var(--btnRadius);
  padding: 10px 14px;
  font-weight: 900;
  display:inline-flex;
  align-items:center;
  gap: 10px;
  cursor:pointer;
  transition: transform .06s ease, opacity .2s ease, box-shadow .2s ease;
  user-select:none;
}
.ta-btn:active{ transform: translateY(1px); }
.ta-btn:disabled{
  opacity: .55;
  cursor:not-allowed;
}
.ta-btnPrimary{
  background: var(--accent);
  color:#fff;
  box-shadow: none;
}
.ta-btnPrimary:hover{ background: #1590c4; }

.ta-btnSecondary{
  background:#ffffff;
  color:#0f172a;
  border:1px solid var(--line);
  box-shadow: none;
}
.ta-btnSecondary:hover{ background: #f8fafc; }

.ta-btnUpdate{
  background: var(--accent);
  color:#fff;
  box-shadow: none;
}
.ta-btnUpdate:hover{ background: #1590c4; }

.ta-btnDelete{
  background: transparent;
  color: #dc2626;
  border: 1px solid #fca5a5;
  box-shadow: none;
}
.ta-btnDelete:hover{
  background: #fef2f2;
  color: #b91c1c;
  border-color: #f87171;
}

.ta-btnDanger{
  background:#111827;
  color:#fff;
  box-shadow: 0 12px 24px rgba(17,24,39,.18);
}
.ta-btnDanger:hover{ box-shadow: 0 14px 28px rgba(17,24,39,.22); }

.ta-toast{
  position: fixed;
  bottom: 18px;
  right: 18px;
  background: #fff;
  border: 1px solid rgba(229,234,242,.95);
  border-radius: 14px;
  padding: 12px 14px;
  display:flex;
  align-items:center;
  gap: 10px;
  box-shadow: var(--shadow);
  z-index: 50;
  max-width: 360px;
}
.ta-toast-success{ color:#0f9f62; }
.ta-toast-error{ color:#b91c1c; }
.ta-toast span{ color:#0f172a; font-weight: 800; }

.ta-completionOverlay{
  position: fixed;
  inset: 0;
  z-index: 10060;
  display: flex;
  align-items: center;
  justify-content: center;
  padding: 20px;
}
.ta-completionBackdrop{
  position: absolute;
  inset: 0;
  border: 0;
  background: rgba(15, 23, 42, 0.45);
  cursor: pointer;
}
.ta-completionCard{
  position: relative;
  width: min(100%, 420px);
  background: #fff;
  border: 1px solid var(--line);
  border-radius: 20px;
  padding: 28px 24px 24px;
  text-align: center;
  box-shadow: 0 24px 48px rgba(15, 23, 42, 0.18);
  animation: taCompletionPop 0.35s ease-out;
}
@keyframes taCompletionPop{
  from { opacity: 0; transform: translateY(12px) scale(0.96); }
  to { opacity: 1; transform: translateY(0) scale(1); }
}
.ta-completionClose{
  position: absolute;
  top: 12px;
  right: 12px;
  width: 32px;
  height: 32px;
  border: 0;
  border-radius: 999px;
  background: #f1f5f9;
  color: #64748b;
  display: inline-flex;
  align-items: center;
  justify-content: center;
  cursor: pointer;
}
.ta-completionIconWrap{
  width: 64px;
  height: 64px;
  margin: 0 auto 14px;
  border-radius: 999px;
  background: linear-gradient(135deg, #ecfdf3, #e8f6fc);
  color: #16a34a;
  display: flex;
  align-items: center;
  justify-content: center;
}
.ta-completionTitle{
  margin: 0 0 8px;
  font-size: 24px;
  font-weight: 800;
  color: var(--text);
}
.ta-completionText{
  margin: 0 0 10px;
  font-size: 14px;
  line-height: 1.55;
  color: var(--muted);
}
.ta-completionRef{
  margin: 0 0 18px;
  font-size: 12px;
  font-family: ui-monospace, SFMono-Regular, Menlo, Monaco, Consolas, monospace;
  color: #94a3b8;
}
.ta-completionBtn{
  width: 100%;
  border: 0;
  border-radius: 12px;
  padding: 12px 16px;
  font-size: 14px;
  font-weight: 700;
  color: #fff;
  background: var(--accent);
  cursor: pointer;
}
.ta-completionBtn:hover{ filter: brightness(0.95); }

.ta-modalOverlay{
  position: fixed;
  inset: 0;
  background: rgba(15, 23, 42, .46);
  display:flex;
  align-items:center;
  justify-content:center;
  padding: 18px;
  z-index: 100;
}
.ta-modal{
  width: min(860px, 100%);
  background:#fff;
  border-radius: 18px;
  box-shadow: 0 20px 60px rgba(0,0,0,.22);
  border: 1px solid rgba(229,234,242,.95);
  overflow:hidden;
}
.ta-modalHeader{
  padding: 14px 16px;
  border-bottom: 1px solid var(--line);
  display:flex;
  align-items:center;
  justify-content:space-between;
}
.ta-modalTitle{
  font-weight: 950;
  letter-spacing: -0.02em;
}
.ta-iconBtn{
  width: 38px;
  height: 38px;
  border-radius: 12px;
  border: 1px solid var(--line);
  background:#fff;
  display:flex;
  align-items:center;
  justify-content:center;
  cursor:pointer;
}
.ta-modalBody{
  padding: 16px;
}
.ta-modalFooter{
  padding: 14px 16px;
  border-top: 1px solid var(--line);
  display:flex;
  justify-content:flex-end;
  gap: 10px;
}

.ta-formGrid{
  display:grid;
  grid-template-columns: repeat(2, minmax(0,1fr));
  gap: 12px;
}
.ta-field{ display:block; }
.ta-fieldLabel{
  font-size: 12px;
  color: var(--muted);
  font-weight: 800;
  margin: 0 0 6px;
}
.ta-input{
  width: 100%;
  border: 1px solid var(--line);
  border-radius: 14px;
  padding: 10px 12px;
  font-weight: 700;
  outline: none;
}
.ta-input:focus{
  border-color: rgba(14, 165, 233, .55);
  box-shadow: 0 0 0 4px rgba(14, 165, 233, .14);
}
.ta-formDivider{
  grid-column: 1 / -1;
  height: 1px;
  background: var(--line);
  margin: 6px 0 2px;
}
.ta-formSectionTitle{
  grid-column: 1 / -1;
  font-weight: 950;
  margin-top: 4px;
}
.ta-formHint{
  margin-top: 12px;
  font-size: 12px;
  color: var(--muted);
}

.ta-dangerBox{
  border: 1px solid rgba(248,113,113,.35);
  background: rgba(254,226,226,.35);
  border-radius: 16px;
  padding: 14px;
}
.ta-dangerTitle{
  font-weight: 950;
  color:#991b1b;
  margin-bottom: 6px;
}
.ta-dangerText{
  color:#7f1d1d;
  font-weight: 800;
  font-size: 13px;
  margin-bottom: 10px;
}
.ta-dangerId{
  font-family: ui-monospace, SFMono-Regular, Menlo, Monaco, Consolas, "Liberation Mono", "Courier New", monospace;
  background: rgba(255,255,255,.7);
  border: 1px dashed rgba(185,28,28,.35);
  padding: 6px 10px;
  border-radius: 12px;
  display:inline-block;
  margin-top: 8px;
}
.ta-dangerNote{
  color: #7f1d1d;
  font-size: 12px;
  margin-top: 10px;
  font-weight: 700;
}

@media (max-width: 980px){
  .ta-factsGrid{ grid-template-columns: repeat(2, minmax(0, 1fr)); }
}

@media (max-width: 767px){
  .ta-kpiProgressSection .ta-progressBlock{
    order: 1;
    margin: 0 0 12px;
  }
  .ta-kpiProgressSection .ta-kpis{
    order: 2;
    grid-template-columns: repeat(2, minmax(0, 1fr));
    gap: 10px;
    margin-bottom: 14px;
  }
  .ta-kpi{
    border-radius: 12px;
    padding: 10px 12px;
    cursor: default;
  }
  .ta-kpi:hover{
    transform: none;
    box-shadow: none;
  }
  .ta-kpi:before{
    inset: -50px -40px auto auto;
    width: 120px;
    height: 120px;
  }
  .ta-kpi:hover:before{
    transform: rotate(22deg);
    background: rgba(255,255,255,.12);
  }
  .ta-kpiLabel{
    font-size: 11px;
    font-weight: 600;
    margin-bottom: 4px;
    line-height: 1.2;
  }
  .ta-kpiValue{
    font-size: 15px;
    font-weight: 700;
    line-height: 1.25;
    word-break: break-word;
  }
}

@media (max-width: 560px){
  .ta-factsGrid{ grid-template-columns: repeat(2, minmax(0, 1fr)); gap: 8px; }
  .ta-factTile{ padding: 10px 12px; }
  .ta-factValue{ font-size: 14px; }
  .ta-kpis{ gap: 8px; }
  .ta-kpi{ padding: 9px 10px; }
  .ta-kpiValue{ font-size: 14px; }
  .ta-card{ padding: 16px; }
  .ta-title{ font-size: 18px; }
  .ta-manager{ flex-direction:column; align-items:flex-start; }
  .ta-managerRight{ justify-content:flex-start; }
  .ta-select{ min-width: 100%; }
  .ta-topbar{ align-items:flex-start; flex-direction: column; }
  .ta-mutedRight{ justify-content:flex-start; }
  .ta-formGrid{ grid-template-columns: 1fr; }
  .ta-illustration{ flex-direction: column; align-items: stretch; text-align: left; }
  .ta-illusLeft{ justify-content: flex-start; }
  .ta-illustration .ta-btn{ width: 100%; justify-content: center; }
}
`;
